/**
 * Trusted Finder Manager
 * Manages the trusted seller search interface
 */

// Initialize logger for this module - with fallback for when logger.js hasn't loaded yet
const logger = (typeof window !== 'undefined' && window.Logger) 
  ? window.Logger.create('trusted-finder') 
  : {
      debug: () => {},
      info: (...args) => console.log('[trusted-finder]', ...args),
      warn: (...args) => console.warn('[trusted-finder]', ...args),
      error: (...args) => console.error('[trusted-finder]', ...args)
    };

import { apiService } from './api-service.js';
import { grid as consoleGrid, rows as consoleRows, toCard } from './console-cards.js';
import { FLOOR_NAMES, getFloor, onFloorChange, setFloor } from './floor-state.js';
import { nextFloorDown } from './console-states.js';
import { postOutboundClick } from './cookieless-page-analytics.js';
import { showOutboundNotice } from './external-link-notice.js';
import {
  parseInput,
  detectMode,
  intentToApiParams,
  intentToDisplayFilters,
  intentToCanonicalInput,
  DEFAULT_INTENT,
  TRUSTED_PRESETS,
  SYNTAX_HELP
} from './trusted-query-parser.js';

class TrustedFinderManager {
  constructor() {
    this.currentPage = 1;
    this.currentParams = {};
    this.currentResults = null; // Cached results for client-side sort (no refetch)
    this.searchDebounceTimer = null;
    this.parseDebounceTimer = null;
    this.isSearching = false;
    this.pendingSearchAfterComplete = false;
    /** @type {import('./trusted-query-parser.js').SearchIntent|null} */
    this.searchIntent = null;
    // Load view preference from localStorage
    try {
      // Rows is the default here, against Tiles everywhere else: this page
      // exists to compare sellers, and the columns are the comparison.
      const savedView = localStorage.getItem('usedfindr_trusted_finder_view');
      this.currentView = (savedView === 'details' || savedView === 'cards') ? savedView : 'details';
    } catch (e) {
      this.currentView = 'details';
    }
    this.currentSortColumn = null; // Column name for table sorting
    this.currentSortDirection = 'asc'; // 'asc' or 'desc'
    
    /*
     * The four trust floors, mirroring TRUSTED_PRESETS in the parser and
     * products/trust_floor.py. `off` is one of the four: the preset control on
     * this page is the global trust floor, and the floor has an off position.
     * intentToApiParams() omits the seller parameters entirely for it rather
     * than sending 0/0 -- see the comment there.
     */
    this.trustedLevels = {
      off: { feedback: 0, reviews: 0 },
      standard: { feedback: 99.0, reviews: 50 },
      strict: { feedback: 99.5, reviews: 50 },
      elite: { feedback: 99.8, reviews: 100 }
    };
    // How the last search divided at the floor: {matched, below, unrated,
    // clears, checked}, straight off the response. Held so renderResults() and
    // showEmptyState() can both say what the floor cost without asking twice.
    this.lastFloorSplit = null;
  }

  /** @returns {boolean} */
  readIncludeForPartsFromDom() {
    const ex = document.getElementById('trustedExcludeForParts');
    if (ex) return !ex.checked;
    const legacy = document.getElementById('trustedIncludeForParts');
    return !!(legacy && legacy.checked);
  }

  /** @param {boolean} include */
  setIncludeForPartsInDom(include) {
    const ex = document.getElementById('trustedExcludeForParts');
    if (ex) ex.checked = !include;
    const legacy = document.getElementById('trustedIncludeForParts');
    if (legacy) legacy.checked = !!include;
  }

  paintTrustedRange(input) {
    if (!input || input.type !== 'range' || !input.hasAttribute('data-fill')) return;
    const min = +input.min || 0;
    const max = +input.max || 100;
    const val = +input.value;
    const pct = max === min ? 0 : ((val - min) / (max - min)) * 100;
    input.style.setProperty('--fill', pct + '%');
  }

  syncDualPriceVisual() {
    const dual = document.getElementById('trustedPriceDual');
    const minIn = document.getElementById('trustedMinPrice');
    const maxIn = document.getElementById('trustedMaxPrice');
    const fill = document.getElementById('trustedPriceFill');
    const tMin = document.getElementById('trustedPriceThumbMin');
    const tMax = document.getElementById('trustedPriceThumbMax');
    const minSlider = document.getElementById('trustedMinPriceSlider');
    const maxSlider = document.getElementById('trustedMaxPriceSlider');
    if (!dual || !minIn || !maxIn || !fill || !tMin || !tMax) return;
    const MAX = 10000;
    const mn = Math.max(0, Math.min(parseFloat(minIn.value) || 0, MAX));
    const mx = Math.max(mn, Math.min(parseFloat(maxIn.value) || MAX, MAX));
    if (minSlider) minSlider.value = String(Math.round(mn / 10) * 10);
    if (maxSlider) maxSlider.value = String(Math.round(mx / 10) * 10);
    const pMin = (mn / MAX) * 100;
    const pMax = (mx / MAX) * 100;
    tMin.style.left = pMin + '%';
    tMax.style.left = pMax + '%';
    fill.style.left = pMin + '%';
    fill.style.right = 100 - pMax + '%';
    tMin.setAttribute('aria-valuenow', String(Math.round(mn)));
    tMax.setAttribute('aria-valuenow', String(Math.round(mx)));
  }

  setupDualPriceSlider() {
    const dual = document.getElementById('trustedPriceDual');
    const tMin = document.getElementById('trustedPriceThumbMin');
    const tMax = document.getElementById('trustedPriceThumbMax');
    const minIn = document.getElementById('trustedMinPrice');
    const maxIn = document.getElementById('trustedMaxPrice');
    if (!dual || !tMin || !tMax || !minIn || !maxIn) return;
    const MAX = 10000;
    let drag = null;

    const pxToVal = (clientX) => {
      const rect = dual.getBoundingClientRect();
      const pad = 9;
      const usable = rect.width - 2 * pad;
      const x = Math.max(0, Math.min(clientX - rect.left - pad, usable));
      const p = usable <= 0 ? 0 : x / usable;
      return Math.round(p * MAX / 10) * 10;
    };

    const onMove = (e) => {
      const v = pxToVal(e.clientX);
      const curMin = parseFloat(minIn.value) || 0;
      const curMax = parseFloat(maxIn.value) || MAX;
      if (drag === 'min') minIn.value = Math.min(v, curMax).toFixed(2);
      else if (drag === 'max') maxIn.value = Math.max(v, curMin).toFixed(2);
      this.syncDualPriceVisual();
      if (this.currentParams.q) this.handleSearch();
    };
    const onUp = () => {
      drag = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    const onDown = (which, e) => {
      e.preventDefault();
      drag = which;
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    };
    tMin.addEventListener('mousedown', e => onDown('min', e));
    tMax.addEventListener('mousedown', e => onDown('max', e));

    [minIn, maxIn].forEach((inp) => {
      inp.addEventListener('input', () => {
        this.syncDualPriceVisual();
      });
    });
    this.syncDualPriceVisual();
  }

  /**
   * Format item end time (ISO string or null) for display: "Ends in X hours/days" or "Ends [local date/time]"
   * @param {string|null|undefined} isoString - ISO 8601 date string (e.g. from item_end_time / itemEndTime)
   * @returns {string} - Human-readable availability text or empty string
   */
  formatItemEndTime(isoString) {
    if (!isoString || typeof isoString !== 'string') return '';
    try {
      const end = new Date(isoString);
      if (Number.isNaN(end.getTime())) return '';
      const now = new Date();
      const ms = end.getTime() - now.getTime();
      if (ms <= 0) return 'Ended';
      const hours = Math.floor(ms / (1000 * 60 * 60));
      const days = Math.floor(hours / 24);
      if (days >= 1) {
        return days === 1 ? 'Ends in 1 day' : `Ends in ${days} days`;
      }
      if (hours >= 1) {
        return hours === 1 ? 'Ends in 1 hour' : `Ends in ${hours} hours`;
      }
      const mins = Math.floor(ms / (1000 * 60));
      if (mins >= 1) return mins === 1 ? 'Ends in 1 min' : `Ends in ${mins} mins`;
      const opts = { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' };
      return `Ends ${end.toLocaleDateString(undefined, opts)}`;
    } catch (e) {
      return '';
    }
  }

  /**
   * Helper function to upgrade eBay image URLs to highest resolution
   * @param {string} url - Original image URL
   * @returns {string} - Upgraded URL with highest available resolution
   */
  getHighResUrl(url) {
    if (!url || typeof url !== 'string') return url;
    
    // eBay thumbnail patterns: s-l64.jpg (tiny) -> s-l225.jpg (small) -> s-l500.jpg (medium) -> s-l1600.jpg (large)
    // Replace with highest resolution available
    if (url.includes('s-l64.jpg')) {
      return url.replace('s-l64.jpg', 's-l1600.jpg');
    } else if (url.includes('s-l225.jpg')) {
      return url.replace('s-l225.jpg', 's-l1600.jpg');
    } else if (url.includes('s-l500.jpg')) {
      return url.replace('s-l500.jpg', 's-l1600.jpg');
    }
    
    // Return original if no pattern matches
    return url;
  }

  /**
   * Initialize the trusted finder
   */
  init() {
    logger.info('[TrustedFinder] Initializing...');
    
    // Debug: key elements
    const welcome = document.getElementById('trustedWelcomeState');
    const filters = document.getElementById('trustedFiltersPanel');
    const resultsContainer = document.querySelector('.trusted-results-container');
    const main = document.querySelector('main');

    logger.info('[TrustedFinder] Element check:', {
      filters: !!filters,
      welcome: !!welcome,
      resultsContainer: !!resultsContainer,
      main: !!main,
    });
    
    this.setupEventListeners();
    this.setupSliders();
    this.setupDualPriceSlider();
    this.loadCustomCriteria();
    this.parseURLParams();
    this.syncDualPriceVisual();
    const qInit = document.getElementById('trustedMinQuantity');
    if (qInit) {
      const qvInit = document.getElementById('trustedMinQuantityVal');
      if (qvInit) qvInit.textContent = qInit.value;
      this.paintTrustedRange(qInit);
    }
    this.setupV2UI();

    // Advanced settings default collapsed on landing (see trusted-finder.html initial classes / aria-expanded).

    this.ensureReturnsAcceptedRequired();

    // Update Trusted Level dropdown state based on custom criteria
    this.syncSellerCriteriaUi();

    this.paintViewToggle();
    
    // The preset control is the global floor; take its value before the first
    // search so the request goes out at the floor the page is claiming.
    this.syncFloorToPreset(getFloor());

    // Ensure welcome state is visible on initial load
    if (welcome) {
      logger.info('[TrustedFinder] Showing welcome state');
      welcome.classList.remove('hidden');
    } else {
      logger.error('[TrustedFinder] Welcome state element not found!');
    }

    // If URL has search params, perform search automatically
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('q')) {
      logger.info('[TrustedFinder] URL has search params, performing search');
      this.setTrustedFinderSlot('loading');
      this.handleSearch();
    } else {
      logger.info('[TrustedFinder] No search params, showing welcome state');
      this.setTrustedFinderSlot('empty');
      // The empty state is a demonstration, not a blank: fill it with what the
      // floor is clearing right now.
      this.renderFloorSample();
    }

    logger.info('[TrustedFinder] Initialization complete');
  }

  /**
   * V2: Populate example queries in welcome state, syntax help panel, wire toggles and copy.
   */
  setupV2UI() {
    const syntaxPanel = document.getElementById('trustedSyntaxHelpPanel');
    if (syntaxPanel && SYNTAX_HELP && SYNTAX_HELP.length > 0) {
      const table = document.createElement('table');
      table.className = 'trusted-syntax-help-table';
      table.setAttribute('aria-label', 'Shorthand syntax reference');
      SYNTAX_HELP.forEach(({ token, desc, example }) => {
        const tr = document.createElement('tr');
        tr.innerHTML = `<td><code>${this.escapeHtml(token)}</code></td><td>${this.escapeHtml(desc)}</td><td><code>${this.escapeHtml(example)}</code></td>`;
        table.appendChild(tr);
      });
      syntaxPanel.appendChild(table);
    }

    const syntaxBtn = document.getElementById('trustedSyntaxHelpBtn');
    const apiBtn = document.getElementById('trustedApiViewBtn');
    const apiPanel = document.getElementById('trustedApiPreviewPanel');
    const copyBtn = document.getElementById('trustedApiCopyBtn');

    if (syntaxBtn && syntaxPanel) {
      syntaxBtn.addEventListener('click', () => {
        const expanded = syntaxBtn.getAttribute('aria-expanded') === 'true';
        syntaxBtn.setAttribute('aria-expanded', String(!expanded));
        syntaxPanel.classList.toggle('hidden', expanded);
        if (apiPanel && !apiPanel.classList.contains('hidden')) {
          apiPanel.classList.add('hidden');
          if (apiBtn) apiBtn.setAttribute('aria-expanded', 'false');
        }
      });
    }
    if (apiBtn && apiPanel) {
      apiBtn.addEventListener('click', () => {
        const expanded = apiBtn.getAttribute('aria-expanded') === 'true';
        apiBtn.setAttribute('aria-expanded', String(!expanded));
        apiPanel.classList.toggle('hidden', expanded);
        if (!expanded) {
          const intent = this.searchIntent ?? this.domToIntent();
          this.updateApiPreview(intent);
        }
        if (syntaxPanel && !syntaxPanel.classList.contains('hidden')) {
          syntaxPanel.classList.add('hidden');
          if (syntaxBtn) syntaxBtn.setAttribute('aria-expanded', 'false');
        }
      });
    }
    if (copyBtn) {
      copyBtn.addEventListener('click', () => {
        const bodyEl = document.getElementById('trustedApiPreviewBody');
        const params = this.searchIntent ? intentToApiParams(this.searchIntent, 1) : null;
        const text = params ? JSON.stringify(params, null, 2) : '';
        if (text && navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(text).then(() => {
            copyBtn.textContent = 'Copied!';
            setTimeout(() => { copyBtn.textContent = 'Copy'; }, 1500);
          });
        } else if (bodyEl && bodyEl.textContent) {
          navigator.clipboard?.writeText(bodyEl.textContent);
          copyBtn.textContent = 'Copied!';
          setTimeout(() => { copyBtn.textContent = 'Copy'; }, 1500);
        }
      });
    }
  }

  /**
   * Make the Console command bar this page's search box.
   *
   * The page shipped with two of them: the command bar at the top of every
   * page, and its own hero search underneath. Two inputs for one job, one of
   * which would silently navigate away to /search.html and drop the trust
   * floor the page exists to apply.
   *
   * The command bar wins, because it is the search on every other page and
   * muscle memory follows it. Its form is a real GET to /search.html so it
   * still works with JavaScript off; here we take the submit, keep the person
   * on the page, and run the query through the parser the hero search used --
   * so `condition:used $900-1300` still means what it means. The hero search
   * is then hidden by console/pages.css, and only under `body.console`: the
   * markup stays for anything that reads the page without the stylesheet.
   */
  adoptTheCommandBar() {
    const inputs = ['mainSearchInput', 'mobileSearchInput']
      .map((id) => document.getElementById(id))
      .filter(Boolean);
    if (!inputs.length) return;
    if (!document.body || !document.body.classList.contains('console')) return;

    const hero = document.getElementById('trustedSearchInput');
    for (const input of inputs) {
      const form = input.closest('form');
      if (!form) continue;
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        const value = input.value || '';
        // The hero input is still the field the rest of this class reads, so
        // it stays the one source of truth for the current query even though
        // nobody can see it.
        if (hero) hero.value = value;
        this.parseAndSyncFromInput(value);
        this.handleSearch();
      });
    }

    // A query already in the URL was typed into the command bar on another
    // page, so run it rather than showing the welcome state under a filled box.
    const initial = inputs[0].value.trim();
    if (initial && hero && !hero.value) {
      hero.value = initial;
      this.parseAndSyncFromInput(initial);
    }
  }

  /**
   * Setup event listeners
   */
  setupEventListeners() {
    logger.info('[TrustedFinder] Setting up event listeners...');
    
    const searchForm = document.getElementById('trustedSearchForm');
    if (searchForm) {
      searchForm.addEventListener('submit', (e) => {
        e.preventDefault();
        this.handleSearch();
      });
    } else {
      logger.warn('[TrustedFinder] trustedSearchForm not found; Enter-to-search may be unavailable');
    }

    this.adoptTheCommandBar();

    // An example query runs. It used to only fill the box, which left the
    // visitor holding a query they did not write and a button still to find.
    document.querySelectorAll('.tf-example').forEach((btn) => {
      btn.addEventListener('click', () => {
        const q = btn.getAttribute('data-q');
        const searchInput = document.getElementById('trustedSearchInput');
        if (!searchInput || q == null || q === '') return;
        searchInput.value = q;
        this.parseAndSyncFromInput(q);
        this.handleSearch();
      });
    });

    this.bindOperatorChips();

    // Search input - Enter key and debounced search
    const searchInput = document.getElementById('trustedSearchInput');
    if (searchInput) {
      searchInput.addEventListener('keypress', (e) => {
        if (e.key === 'Enter') {
          this.handleSearch();
        }
      });

      // Debounced search (optional - could trigger on typing)
      // searchInput.addEventListener('input', () => {
      //   clearTimeout(this.searchDebounceTimer);
      //   this.searchDebounceTimer = setTimeout(() => {
      //     this.handleSearch();
      //   }, 500);
      // });
    }

    // Filter changes trigger search
    const listingTypeToggle = document.getElementById('trustedListingTypeToggle');
    if (listingTypeToggle) {
      listingTypeToggle.addEventListener('change', () => {
        this.updateFilterSummaryChips();
        if (this.currentParams.q) {
          this.handleSearch();
        }
      });
    }

    const conditionRadios = document.querySelectorAll('input[name="trustedCondition"]');
    conditionRadios.forEach(radio => {
      radio.addEventListener('change', () => {
        this.updateFilterSummaryChips();
        if (this.currentParams.q) {
          this.handleSearch();
        }
      });
    });

    const returnsAcceptedCheckbox = document.getElementById('trustedReturnsAccepted');
    if (returnsAcceptedCheckbox) {
      returnsAcceptedCheckbox.addEventListener('change', () => {
        this.ensureReturnsAcceptedRequired();
        this.syncSellerCriteriaUi();
        if (this.currentParams.q) this.handleSearch();
      });
    }

    const freeShippingCheckbox = document.getElementById('trustedFreeShipping');
    if (freeShippingCheckbox) {
      freeShippingCheckbox.addEventListener('change', () => {
        this.updateFilterSummaryChips();
        if (this.currentParams.q) this.handleSearch();
      });
    }

    const minQuantityInput = document.getElementById('trustedMinQuantity');
    if (minQuantityInput) {
      minQuantityInput.addEventListener('input', () => {
        const qv = document.getElementById('trustedMinQuantityVal');
        if (qv) qv.textContent = minQuantityInput.value;
        this.paintTrustedRange(minQuantityInput);
        if (this.currentParams.q) this.handleSearch();
      });
      this.paintTrustedRange(minQuantityInput);
    }

    const excludeForParts = document.getElementById('trustedExcludeForParts');
    if (excludeForParts) {
      excludeForParts.addEventListener('change', () => {
        if (this.currentParams.q) this.handleSearch();
      });
    }

    const trustedLevelSelect = document.getElementById('trustedLevelSelect');
    if (trustedLevelSelect) {
      trustedLevelSelect.addEventListener('change', () => {
        this.syncPresetToInlineDisplays();
        this.syncSellerCriteriaUi();
        if (this.currentParams.q) this.handleSearch();
      });
    }

    // Sort order (client-side only: re-sort cached results, no API call)
    const sortOrderSelect = document.getElementById('trustedSortOrder');
    if (sortOrderSelect) {
      sortOrderSelect.addEventListener('change', () => {
        this.handleSortChange();
        // The status line names the order, so it is part of what changed.
        if (this.currentResults) this.renderResultsHeading(this.currentResults, { trust_floor: this.lastFloorSplit });
      });
    }

    /*
     * The preset is the trust floor, so this page listens rather than owning
     * it: the pane's radios and the top bar's segments both go through
     * setFloor(), and both arrive here. Re-running on change is the point --
     * a floor you can change without the results moving is a floor that is not
     * doing anything.
     */
    onFloorChange((floor) => this.syncFloorToPreset(floor, { rerun: true }));

    /*
     * One delegated listener for the Refine group's enabled/disabled state.
     *
     * The individual controls already have handlers that re-run the search;
     * this is only about whether `reset` is offered, and every control in the
     * group answers that the same way. Binding it once on the group means a
     * fifth filter added later is covered without a fifth handler.
     */
    const refine = document.querySelector('.pane .tf-refine');
    if (refine) {
      const repaint = () => this.updateFiltersDirtyAndReset();
      refine.addEventListener('change', repaint);
      refine.addEventListener('input', repaint);
    }

    // View toggle buttons
    const cardsViewBtn = document.getElementById('trustedViewCards');
    const detailsViewBtn = document.getElementById('trustedViewDetails');
    if (cardsViewBtn) {
      cardsViewBtn.addEventListener('click', () => this.handleViewToggle('cards'));
    }
    if (detailsViewBtn) {
      detailsViewBtn.addEventListener('click', () => this.handleViewToggle('details'));
    }

    // Column sort handlers for table view (using event delegation)
    // Set up listener on table container so it works even if table is dynamically created
    const tableContainer = document.getElementById('trustedResultsTable');
    if (tableContainer) {
      tableContainer.addEventListener('click', (e) => {
        const header = e.target.closest('th.sortable');
        if (header) {
          const columnName = header.getAttribute('data-sort');
          if (columnName) {
            this.handleColumnSort(columnName);
          }
        }
      });
    }

    // Price inputs
    const minPriceInput = document.getElementById('trustedMinPrice');
    const maxPriceInput = document.getElementById('trustedMaxPrice');
    if (minPriceInput) {
      minPriceInput.addEventListener('change', () => {
        this.updateFilterSummaryChips();
        if (this.currentParams.q) {
          this.handleSearch();
        }
      });
    }
    if (maxPriceInput) {
      maxPriceInput.addEventListener('change', () => {
        this.updateFilterSummaryChips();
        if (this.currentParams.q) {
          this.handleSearch();
        }
      });
    }

    // Pagination
    const prevBtn = document.getElementById('trustedPrevBtn');
    const nextBtn = document.getElementById('trustedNextBtn');
    if (prevBtn) {
      prevBtn.addEventListener('click', () => this.handlePagination('prev'));
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', () => this.handlePagination('next'));
    }

    // Retry button
    const retryBtn = document.getElementById('trustedRetryBtn');
    if (retryBtn) {
      retryBtn.addEventListener('click', () => this.handleSearch());
    }

    // File protocol warning close button
    const fileProtocolWarning = document.getElementById('fileProtocolWarning');
    if (fileProtocolWarning) {
      const closeBtn = fileProtocolWarning.querySelector('.file-protocol-warning-btn');
      if (closeBtn) {
        closeBtn.addEventListener('click', () => {
          fileProtocolWarning.classList.add('hidden');
        });
      }
    }

    // Add item modal handlers
    const globalAddItemBtn = document.getElementById('globalAddItemBtn');
    if (globalAddItemBtn) {
      globalAddItemBtn.addEventListener('click', (e) => {
        e.preventDefault();
        if (typeof window.openAddItemModalGlobal === 'function') {
          window.openAddItemModalGlobal();
        } else if (typeof AccountManager !== 'undefined' && AccountManager.openAddItemModalGlobal) {
          AccountManager.openAddItemModalGlobal();
        }
      });
    }

    const addItemModalClose = document.getElementById('addItemModalClose');
    const addItemModalCancel = document.getElementById('addItemModalCancel');
    if (addItemModalClose) {
      addItemModalClose.addEventListener('click', (e) => {
        e.preventDefault();
        if (typeof window.closeAddItemModalGlobal === 'function') {
          window.closeAddItemModalGlobal();
        } else if (typeof AccountManager !== 'undefined' && AccountManager.closeAddItemModalGlobal) {
          AccountManager.closeAddItemModalGlobal();
        }
      });
    }
    if (addItemModalCancel) {
      addItemModalCancel.addEventListener('click', (e) => {
        e.preventDefault();
        if (typeof window.closeAddItemModalGlobal === 'function') {
          window.closeAddItemModalGlobal();
        } else if (typeof AccountManager !== 'undefined' && AccountManager.closeAddItemModalGlobal) {
          AccountManager.closeAddItemModalGlobal();
        }
      });
    }

    // Modal content click handler (stop propagation)
    const modalContent = document.querySelector('#addItemModal .modal-content');
    if (modalContent) {
      modalContent.addEventListener('click', (e) => {
        e.stopPropagation();
      });
    }

    // Report modal handlers
    const reportModalClose = document.querySelector('#reportModal .modal-close');
    const reportModalCancel = document.querySelector('#reportModal .btn-secondary');
    if (reportModalClose) {
      reportModalClose.addEventListener('click', () => {
        if (window.reportManager && typeof window.reportManager.closeReportModal === 'function') {
          window.reportManager.closeReportModal();
        }
      });
    }
    if (reportModalCancel) {
      reportModalCancel.addEventListener('click', () => {
        if (window.reportManager && typeof window.reportManager.closeReportModal === 'function') {
          window.reportManager.closeReportModal();
        }
      });
    }

    // Service worker update handlers
    const reloadForUpdateBtn = document.querySelector('.sw-update-btn-primary');
    const dismissUpdateBtn = document.querySelector('.sw-update-btn-secondary');
    if (reloadForUpdateBtn) {
      reloadForUpdateBtn.addEventListener('click', () => {
        if (typeof window.reloadForUpdate === 'function') {
          window.reloadForUpdate();
        }
      });
    }
    if (dismissUpdateBtn) {
      dismissUpdateBtn.addEventListener('click', () => {
        if (typeof window.dismissUpdate === 'function') {
          window.dismissUpdate();
        }
      });
    }

    // Empty state "Try Another Search" button
    const emptyStateBtn = document.querySelector('#trustedEmptyState .btn-primary');
    if (emptyStateBtn) {
      emptyStateBtn.addEventListener('click', () => {
        const searchInput = document.getElementById('trustedSearchInput');
        if (searchInput) {
          searchInput.focus();
        }
      });
    }

    // V2: Syntax help and API preview toggles are wired in setupV2UI() to avoid duplicate handlers.
    // Do not add them here—duplicate listeners cause double-toggle (open then immediately close).

    // Reset to preset button
    const resetToPresetBtn = document.getElementById('trustedResetToPresetBtn');
    if (resetToPresetBtn) {
      resetToPresetBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.resetToDefaults();
      });
    }

    // Advanced Settings collapsible toggle
    const advancedSettingsToggle = document.getElementById('advancedSettingsToggle');
    const advancedSettingsContent = document.getElementById('advancedSettingsContent');
    const filtersContainer = document.querySelector('.trusted-filters-container');
    const filtersHeadEl = document.getElementById('filtersHead');

    if (advancedSettingsToggle && advancedSettingsContent && advancedSettingsToggle.dataset.listenerAttached !== 'true') {
      advancedSettingsToggle.dataset.listenerAttached = 'true';

      advancedSettingsToggle.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        const isExpanded = advancedSettingsToggle.getAttribute('aria-expanded') === 'true';
        const willBeCollapsed = isExpanded;

        advancedSettingsToggle.setAttribute('aria-expanded', String(!isExpanded));

        if (willBeCollapsed) {
          advancedSettingsContent.classList.add('collapsed');
          if (filtersContainer) filtersContainer.classList.add('filters-collapsed');
          setTimeout(() => {
            advancedSettingsContent.classList.add('hidden');
          }, 400);
        } else {
          advancedSettingsContent.classList.remove('hidden');
          void advancedSettingsContent.offsetHeight;
          advancedSettingsContent.classList.remove('collapsed');
          if (filtersContainer) filtersContainer.classList.remove('filters-collapsed');
        }
        this.updateFilterSummaryChips();
      });
    }

    const adjustFiltersBtn = document.getElementById('tfAdjustFiltersBtn');
    if (adjustFiltersBtn && advancedSettingsToggle && adjustFiltersBtn.dataset.listenerAttached !== 'true') {
      adjustFiltersBtn.dataset.listenerAttached = 'true';
      adjustFiltersBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (advancedSettingsToggle.getAttribute('aria-expanded') !== 'true') {
          advancedSettingsToggle.click();
        }
      });
    }

    // Setup inline criteria sliders (in Advanced Settings)
    this.setupCriteriaSliders();
    this.setupInlineCriteriaListeners();
    this.setupFiltersSheet();

    // V2: Input listener for parse/sync (debounced)
    if (searchInput) {
      searchInput.addEventListener('input', () => {
        clearTimeout(this.parseDebounceTimer);
        this.parseDebounceTimer = setTimeout(() => {
          this.parseAndSyncFromInput(searchInput.value);
        }, 150);
      });
    }
  }

  /**
   * Setup inline criteria sliders (Advanced Settings) - sync slider, number input, and display span
   */
  setupCriteriaSliders() {
    const feedbackSlider = document.getElementById('trustedCriteriaFeedbackSlider');
    const feedbackInput = document.getElementById('trustedCriteriaFeedback');
    const feedbackDisplay = document.getElementById('trustedCriteriaFeedbackDisplay');
    const reviewsSlider = document.getElementById('trustedCriteriaReviewsSlider');
    const reviewsInput = document.getElementById('trustedCriteriaReviews');
    const reviewsDisplay = document.getElementById('trustedCriteriaReviewsDisplay');

    if (!feedbackSlider || !feedbackInput || !reviewsSlider || !reviewsInput) return;

    const updateFeedbackDisplay = (value) => {
      if (feedbackDisplay) feedbackDisplay.textContent = `${value.toFixed(1)}`;
    };
    const updateReviewsDisplay = (value) => {
      if (reviewsDisplay) reviewsDisplay.textContent = String(value);
    };

    // Feedback slider sync
    feedbackSlider.addEventListener('input', (e) => {
      const value = parseFloat(e.target.value);
      feedbackInput.value = value.toFixed(1);
      updateFeedbackDisplay(value);
      this.paintTrustedRange(feedbackSlider);
      this.syncSellerCriteriaUi();
    });

    feedbackInput.addEventListener('input', (e) => {
      let value = parseFloat(e.target.value);
      if (isNaN(value)) return;
      value = Math.max(0, Math.min(100, value));
      feedbackSlider.value = value;
      feedbackInput.value = value.toFixed(1);
      updateFeedbackDisplay(value);
      this.paintTrustedRange(feedbackSlider);
      this.syncSellerCriteriaUi();
    });

    // Reviews slider sync
    reviewsSlider.addEventListener('input', (e) => {
      const value = parseInt(e.target.value, 10);
      reviewsInput.value = value;
      updateReviewsDisplay(value);
      this.paintTrustedRange(reviewsSlider);
      this.syncSellerCriteriaUi();
    });

    reviewsInput.addEventListener('input', (e) => {
      let value = parseInt(e.target.value, 10);
      if (isNaN(value)) return;
      value = Math.max(0, value);
      reviewsSlider.value = Math.min(500, value);
      reviewsInput.value = value;
      updateReviewsDisplay(value);
      this.paintTrustedRange(reviewsSlider);
      this.syncSellerCriteriaUi();
    });

    this.paintTrustedRange(feedbackSlider);
    this.paintTrustedRange(reviewsSlider);
  }

  /**
   * Setup listeners for inline criteria inputs - persist and optionally re-search on change
   */
  setupInlineCriteriaListeners() {
    const feedbackSlider = document.getElementById('trustedCriteriaFeedbackSlider');
    const feedbackInput = document.getElementById('trustedCriteriaFeedback');
    const reviewsSlider = document.getElementById('trustedCriteriaReviewsSlider');
    const reviewsInput = document.getElementById('trustedCriteriaReviews');

    const onCriteriaChange = () => {
      this.saveCustomCriteriaFromInline();
      this.syncSellerCriteriaUi();
      if (this.currentParams.q) this.handleSearch();
    };

    if (feedbackSlider) feedbackSlider.addEventListener('change', onCriteriaChange);
    if (feedbackInput) feedbackInput.addEventListener('change', onCriteriaChange);
    if (reviewsSlider) reviewsSlider.addEventListener('change', onCriteriaChange);
    if (reviewsInput) reviewsInput.addEventListener('change', onCriteriaChange);
  }

  /**
   * Setup price range sliders
   */
  setupSliders() {
    const minSlider = document.getElementById('trustedMinPriceSlider');
    const maxSlider = document.getElementById('trustedMaxPriceSlider');
    const minInput = document.getElementById('trustedMinPrice');
    const maxInput = document.getElementById('trustedMaxPrice');
    const minDisplay = document.getElementById('trustedMinPriceDisplay');
    const maxDisplay = document.getElementById('trustedMaxPriceDisplay');

    if (!minSlider || !maxSlider || !minInput || !maxInput) return;

    // Update display span from slider value (slider values are now in dollars, not cents)
    const updateMinDisplay = (value) => {
      if (minDisplay) {
        const dollarValue = parseFloat(value);
        minDisplay.textContent = `$${dollarValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      }
    };

    const updateMaxDisplay = (value) => {
      if (maxDisplay) {
        const dollarValue = parseFloat(value);
        maxDisplay.textContent = `$${dollarValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      }
    };

    // Slider → Input sync (slider values are in dollars)
    minSlider.addEventListener('input', (e) => {
      const value = parseFloat(e.target.value);
      minInput.value = value.toFixed(2);
      updateMinDisplay(value);
      
      // Ensure min doesn't exceed max
      const maxValue = parseFloat(maxInput.value) || 10000;
      if (value > maxValue) {
        minInput.value = maxValue.toFixed(2);
        minSlider.value = maxValue;
        updateMinDisplay(maxValue);
      }
      this.syncDualPriceVisual();
      this.updateFilterSummaryChips();
    });

    maxSlider.addEventListener('input', (e) => {
      const value = parseFloat(e.target.value);
      maxInput.value = value.toFixed(2);
      updateMaxDisplay(value);
      
      // Ensure max doesn't go below min
      const minValue = parseFloat(minInput.value) || 0;
      if (value < minValue) {
        maxInput.value = minValue.toFixed(2);
        maxSlider.value = minValue;
        updateMaxDisplay(minValue);
      }
      this.syncDualPriceVisual();
      this.updateFilterSummaryChips();
    });

    // Input → Slider sync (input values are in dollars)
    minInput.addEventListener('input', (e) => {
      let value = parseFloat(e.target.value);
      if (isNaN(value)) return;
      
      const sliderMin = parseFloat(minSlider.min);
      const sliderMax = parseFloat(minSlider.max);
      
      if (value >= sliderMin && value <= sliderMax) {
        minSlider.value = value;
        updateMinDisplay(value);
      } else if (value < sliderMin) {
        minSlider.value = sliderMin;
        minInput.value = sliderMin.toFixed(2);
        updateMinDisplay(sliderMin);
      } else if (value > sliderMax) {
        minSlider.value = sliderMax;
        minInput.value = sliderMax.toFixed(2);
        updateMinDisplay(sliderMax);
      }
      this.syncDualPriceVisual();
      this.updateFilterSummaryChips();
    });

    maxInput.addEventListener('input', (e) => {
      let value = parseFloat(e.target.value);
      if (isNaN(value)) return;
      
      const sliderMin = parseFloat(maxSlider.min);
      const sliderMax = parseFloat(maxSlider.max);
      
      if (value >= sliderMin && value <= sliderMax) {
        maxSlider.value = value;
        updateMaxDisplay(value);
      } else if (value < sliderMin) {
        maxSlider.value = sliderMin;
        maxInput.value = sliderMin.toFixed(2);
        updateMaxDisplay(sliderMin);
      } else if (value > sliderMax) {
        maxSlider.value = sliderMax;
        maxInput.value = sliderMax.toFixed(2);
        updateMaxDisplay(sliderMax);
      }
      this.syncDualPriceVisual();
      this.updateFilterSummaryChips();
    });

    // Initialize displays
    updateMinDisplay(minSlider.value);
    updateMaxDisplay(maxSlider.value);
    this.syncDualPriceVisual();
  }

  /**
   * Returns accepted is required for Trusted Finder searches (always enforced).
   */
  ensureReturnsAcceptedRequired(intent = null) {
    if (intent) intent.returnsAccepted = true;
    if (this.searchIntent) this.searchIntent.returnsAccepted = true;
    const returnsCheckbox = document.getElementById('trustedReturnsAccepted');
    if (returnsCheckbox) {
      returnsCheckbox.checked = true;
      returnsCheckbox.disabled = true;
      returnsCheckbox.setAttribute('aria-disabled', 'true');
    }
  }

  /**
   * @returns {import('./trusted-query-parser.js').SearchIntent}
   */
  domToIntent() {
    const searchInput = document.getElementById('trustedSearchInput');
    const minPriceInput = document.getElementById('trustedMinPrice');
    const maxPriceInput = document.getElementById('trustedMaxPrice');
    const listingTypeToggle = document.getElementById('trustedListingTypeToggle');
    const conditionRadios = document.querySelectorAll('input[name="trustedCondition"]:checked');
    const trustedLevelSelect = document.getElementById('trustedLevelSelect');
    const returnsAcceptedCheckbox = document.getElementById('trustedReturnsAccepted');
    const freeShippingCheckbox = document.getElementById('trustedFreeShipping');
    const minQuantityInput = document.getElementById('trustedMinQuantity');
    const feedbackInput = document.getElementById('trustedCriteriaFeedback');
    const reviewsInput = document.getElementById('trustedCriteriaReviews');
    const sortSelect = document.getElementById('trustedSortOrder');

    const query = searchInput ? searchInput.value.trim() : '';
    const minPrice = minPriceInput ? parseFloat(minPriceInput.value) : null;
    const maxPrice = maxPriceInput ? parseFloat(maxPriceInput.value) : null;
    const listingType = listingTypeToggle && listingTypeToggle.checked ? 'FIXED_PRICE' : 'BOTH';
    const condition = conditionRadios.length > 0 ? conditionRadios[0].value : 'all';
    const returnsAccepted = true;
    const freeShipping = !!(freeShippingCheckbox && freeShippingCheckbox.checked);
    const qty = minQuantityInput ? parseInt(minQuantityInput.value, 10) : 1;
    const minQuantity = !isNaN(qty) && qty >= 1 ? Math.min(999, qty) : 1;
    const includeForParts = this.readIncludeForPartsFromDom();
    const trustedLevel = trustedLevelSelect ? trustedLevelSelect.value : 'strict';
    const levelConfig = this.trustedLevels[trustedLevel] || this.trustedLevels.strict;
    const feedbackVal = feedbackInput ? parseFloat(feedbackInput.value) : NaN;
    const reviewsVal = reviewsInput ? parseInt(reviewsInput.value, 10) : NaN;
    const feedbackDiffers = !isNaN(feedbackVal) && feedbackVal !== levelConfig.feedback;
    const reviewsDiffers = !isNaN(reviewsVal) && reviewsVal >= 0 && reviewsVal !== levelConfig.reviews;
    const minSellerFeedbackPct = feedbackDiffers ? Math.max(0, Math.min(100, feedbackVal)) : null;
    const minSellerReviews = reviewsDiffers ? reviewsVal : null;
    const validSorts = ['priceAscending', 'priceDescending', 'sellerScoreDescending', 'newlyListed', 'bestMatch'];
    const sortVal = sortSelect ? sortSelect.value : 'priceAscending';
    const sortOrder = validSorts.includes(sortVal) ? sortVal : 'priceAscending';

    return {
      ...DEFAULT_INTENT,
      query,
      minPrice: minPrice && minPrice > 0 ? minPrice : null,
      maxPrice: maxPrice && maxPrice < 10000 ? maxPrice : null,
      listingType,
      condition: condition === 'all' ? 'all' : condition,
      returnsAccepted,
      freeShipping,
      minQuantity,
      includeForParts,
      trustedLevel,
      minSellerFeedbackPct,
      minSellerReviews,
      sortOrder,
      pageSize: 20
    };
  }

  /**
   * Refresh searchIntent from DOM (e.g. when user changes sliders).
   */
  refreshIntentFromDOM() {
    this.searchIntent = this.domToIntent();
  }

  /**
   * Parse input, update searchIntent, sync to DOM, and update badge/pills/preview.
   * Debounced by caller.
   * @param {string} raw
   */
  /**
   * "Add a filter" chips: each appends its operator token to the query.
   *
   * Only if the query does not already carry that operator -- a second
   * `condition:` would be a conflict the parser resolves silently, and a chip
   * that quietly did nothing on the second press is better than one that
   * quietly changed the meaning of the first. It does not search: the visitor
   * is still composing, and the box keeps focus with the caret at the end so
   * they can edit the value the chip put there.
   */
  bindOperatorChips() {
    const input = document.getElementById('trustedSearchInput');
    if (!input) return;
    document.querySelectorAll('[data-tf-op]').forEach((chip) => {
      chip.addEventListener('click', () => {
        const token = chip.getAttribute('data-tf-op') || '';
        const key = (chip.getAttribute('data-tf-op-key') || token).toLowerCase();
        const current = input.value;
        const lower = current.toLowerCase();
        const present = key === '$' ? /(^|\s)<?\$\d/.test(current) : lower.includes(key);
        if (token && !present) {
          input.value = current.trim() ? `${current.trim()} ${token}` : token;
          this.parseAndSyncFromInput(input.value);
        }
        input.focus();
        const end = input.value.length;
        try { input.setSelectionRange(end, end); } catch (_) { /* type=search in old engines */ }
      });
    });
  }

  parseAndSyncFromInput(raw) {
    const result = parseInput(raw);

    // Plain keyword typing must not reset Advanced Settings to DEFAULT_INTENT.
    if (result.mode === 'text' && !result.error) {
      const fromDom = this.domToIntent();
      const merged = {
        ...fromDom,
        query: (result.intent.query || '').trim(),
      };
      this.searchIntent = merged;
      this.ensureReturnsAcceptedRequired(this.searchIntent);
      this.updateModeBadge(result.mode, result.error);
      this.renderFilterPills(merged);
      this.updateParseStatus(result.warnings, result.error);
      const apiPreviewPanel = document.getElementById('trustedApiPreviewPanel');
      if (apiPreviewPanel && !apiPreviewPanel.classList.contains('hidden')) {
        this.updateApiPreview(merged);
      }
      return;
    }

    if (result.mode === 'shorthand' && /returns\s*:\s*no/i.test(raw)) {
      result.warnings.push({
        code: 'returns_required',
        message: 'Returns accepted is required; returns:no ignored',
        blocking: false,
      });
      result.intent.returnsAccepted = true;
    }

    this.searchIntent = { ...result.intent };
    this.ensureReturnsAcceptedRequired(this.searchIntent);
    this.syncIntentToDOM(this.searchIntent);
    this.updateModeBadge(result.mode, result.error);
    this.renderFilterPills(this.searchIntent);
    this.updateParseStatus(result.warnings, result.error);
    const apiPreviewPanel = document.getElementById('trustedApiPreviewPanel');
    if (apiPreviewPanel && !apiPreviewPanel.classList.contains('hidden')) {
      this.updateApiPreview(this.searchIntent);
    }
  }

  /**
   * Sync intent to Advanced Settings DOM controls.
   * @param {import('./trusted-query-parser.js').SearchIntent} intent
   */
  syncIntentToDOM(intent) {
    if (!intent) return;
    const minInput = document.getElementById('trustedMinPrice');
    const minSlider = document.getElementById('trustedMinPriceSlider');
    const minDisplay = document.getElementById('trustedMinPriceDisplay');
    const maxInput = document.getElementById('trustedMaxPrice');
    const maxSlider = document.getElementById('trustedMaxPriceSlider');
    const maxDisplay = document.getElementById('trustedMaxPriceDisplay');
    const listingToggle = document.getElementById('trustedListingTypeToggle');
    const conditionRadios = document.querySelectorAll('input[name="trustedCondition"]');
    const returnsCheckbox = document.getElementById('trustedReturnsAccepted');
    const levelSelect = document.getElementById('trustedLevelSelect');
    const feedbackInput = document.getElementById('trustedCriteriaFeedback');
    const feedbackSlider = document.getElementById('trustedCriteriaFeedbackSlider');
    const feedbackDisplay = document.getElementById('trustedCriteriaFeedbackDisplay');
    const reviewsInput = document.getElementById('trustedCriteriaReviews');
    const reviewsSlider = document.getElementById('trustedCriteriaReviewsSlider');
    const reviewsDisplay = document.getElementById('trustedCriteriaReviewsDisplay');
    const sortSelect = document.getElementById('trustedSortOrder');

    const minVal = intent.minPrice != null ? intent.minPrice : 0;
    const maxVal = intent.maxPrice != null ? intent.maxPrice : 10000;
    if (minInput) minInput.value = minVal.toFixed(2);
    if (minSlider) minSlider.value = minVal;
    if (minDisplay) minDisplay.textContent = `$${minVal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    if (maxInput) maxInput.value = maxVal.toFixed(2);
    if (maxSlider) maxSlider.value = maxVal;
    if (maxDisplay) maxDisplay.textContent = `$${maxVal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

    if (listingToggle) listingToggle.checked = intent.listingType === 'FIXED_PRICE';
    conditionRadios.forEach(r => { r.checked = r.value === (intent.condition || 'all'); });
    if (returnsCheckbox) returnsCheckbox.checked = true;
    const freeShippingCheckbox = document.getElementById('trustedFreeShipping');
    const minQuantityInput = document.getElementById('trustedMinQuantity');
    if (freeShippingCheckbox) freeShippingCheckbox.checked = !!intent.freeShipping;
    if (minQuantityInput) {
      const mq = intent.minQuantity ?? 1;
      minQuantityInput.value = String(Math.min(20, Math.max(1, mq)));
      const qv = document.getElementById('trustedMinQuantityVal');
      if (qv) qv.textContent = minQuantityInput.value;
      this.paintTrustedRange(minQuantityInput);
    }
    this.setIncludeForPartsInDom(!!intent.includeForParts);
    if (levelSelect && intent.trustedLevel) levelSelect.value = intent.trustedLevel;

    const preset = TRUSTED_PRESETS[intent.trustedLevel] || TRUSTED_PRESETS.strict;
    const fb = intent.minSellerFeedbackPct ?? preset.feedback;
    const rv = intent.minSellerReviews ?? preset.reviews;
    if (feedbackInput) feedbackInput.value = fb.toFixed(1);
    if (feedbackSlider) feedbackSlider.value = fb;
    if (feedbackDisplay) feedbackDisplay.textContent = `${fb.toFixed(1)}`;
    if (reviewsInput) reviewsInput.value = String(rv);
    if (reviewsSlider) reviewsSlider.value = Math.min(500, rv);
    if (reviewsDisplay) reviewsDisplay.textContent = String(rv);

    if (feedbackSlider) this.paintTrustedRange(feedbackSlider);
    if (reviewsSlider) this.paintTrustedRange(reviewsSlider);

    if (sortSelect && intent.sortOrder) sortSelect.value = intent.sortOrder;
    this.syncDualPriceVisual();
    this.ensureReturnsAcceptedRequired(intent);
    this.syncSellerCriteriaUi();
  }

  /**
   * Update mode badge (shorthand/json/invalid).
   * @param {string} mode
   * @param {string|null} error
   */
  updateModeBadge(mode, error) {
    const badge = document.getElementById('trustedSearchMode');
    if (!badge) return;
    if (mode === 'text' || (mode === 'invalid' && !error)) {
      badge.classList.add('hidden');
      badge.textContent = '';
      return;
    }
    badge.classList.remove('hidden');
    badge.textContent = mode === 'invalid' ? 'Invalid' : mode;
    badge.className = 'trusted-search-mode-badge trusted-search-mode-' + (mode === 'invalid' ? 'invalid' : mode);
  }

  /**
   * Render filter pills from intent.
   * @param {import('./trusted-query-parser.js').SearchIntent} intent
   */
  renderFilterPills(intent) {
    const container = document.getElementById('trustedFilterPills');
    if (!container) return;
    const pills = intentToDisplayFilters(intent);
    if (pills.length === 0) {
      container.classList.add('hidden');
      container.innerHTML = '';
      return;
    }
    container.classList.remove('hidden');
    container.innerHTML = '';
    pills.forEach(p => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'trusted-filter-pill';
      btn.setAttribute('data-pill-key', p.key);
      btn.setAttribute('aria-label', `Remove ${p.label}: ${p.value}`);
      btn.innerHTML = `<span class="trusted-filter-pill-label">${this.escapeHtml(p.label)}: ${this.escapeHtml(p.value)}</span> <span class="trusted-filter-pill-remove" aria-hidden="true">×</span>`;
      btn.addEventListener('click', () => this.removeFilterPill(p.key));
      container.appendChild(btn);
    });
  }

  /**
   * Remove a filter pill and regenerate input.
   * @param {string} key
   */
  removeFilterPill(key) {
    if (!this.searchIntent) return;
    if (key === 'returnsAccepted') return;
    const next = { ...this.searchIntent };
    if (key === 'minPrice') next.minPrice = null;
    else if (key === 'maxPrice') next.maxPrice = null;
    else if (key === 'listingType') next.listingType = 'FIXED_PRICE';
    else if (key === 'condition') next.condition = 'all';
    else if (key === 'freeShipping') next.freeShipping = false;
    else if (key === 'minQuantity') next.minQuantity = 1;
    else if (key === 'includeForParts') next.includeForParts = false;
    else if (key === 'trustedLevel') next.trustedLevel = 'strict';
    else if (key === 'sortOrder') next.sortOrder = 'priceAscending';
    else if (key === 'categoryId') next.categoryId = null;
    else if (key === 'pageSize') next.pageSize = 20;
    this.searchIntent = next;
    const canonical = intentToCanonicalInput(next);
    const searchInput = document.getElementById('trustedSearchInput');
    if (searchInput) searchInput.value = canonical;
    this.syncIntentToDOM(next);
    this.renderFilterPills(next);
    this.updateModeBadge(detectMode(canonical), null);
    this.updateApiPreview(next);
  }

  /**
   * Update parse status / warnings region.
   * @param {Array<{code: string, message: string, blocking?: boolean}>} warnings
   * @param {string|null} error
   */
  updateParseStatus(warnings, error) {
    const region = document.getElementById('trustedParseStatus');
    if (!region) return;
    if (error) {
      region.classList.remove('hidden');
      region.textContent = error;
      region.className = 'trusted-parse-status trusted-parse-error';
      return;
    }
    if (warnings && warnings.length > 0) {
      region.classList.remove('hidden');
      region.textContent = warnings.map(w => w.message).join('; ');
      region.className = 'trusted-parse-status trusted-parse-warning';
      return;
    }
    region.classList.add('hidden');
    region.textContent = '';
  }

  /**
   * Build and display API preview.
   * @param {import('./trusted-query-parser.js').SearchIntent} intent
   */
  updateApiPreview(intent) {
    const bodyEl = document.getElementById('trustedApiPreviewBody');
    if (!bodyEl) return;
    const params = intentToApiParams(intent, this.currentPage);
    if (!params) {
      bodyEl.textContent = 'Enter a search term to see API payload.';
      return;
    }
    const qs = new URLSearchParams();
    qs.set('q', params.q);
    if (params.minPrice != null) qs.set('min_price', String(params.minPrice));
    if (params.maxPrice != null) qs.set('max_price', String(params.maxPrice));
    if (params.listingType) qs.set('listing_type', params.listingType);
    if (params.condition) qs.set('condition', params.condition);
    if (params.returnsAccepted) qs.set('returns_accepted', 'true');
    if (params.freeShipping) qs.set('free_shipping', 'true');
    if (params.minQuantity != null && params.minQuantity > 1) qs.set('min_quantity', String(params.minQuantity));
    if (params.includeForParts) qs.set('include_for_parts', 'true');
    if (params.minSellerFeedbackPct != null) qs.set('min_seller_feedback_pct', String(params.minSellerFeedbackPct));
    if (params.minSellerReviews != null) qs.set('min_seller_reviews', String(params.minSellerReviews));
    if (params.sortOrder) qs.set('sort_order', params.sortOrder);
    if (params.page) qs.set('page', String(params.page));
    if (params.pageSize) qs.set('page_size', String(params.pageSize));
    if (params.categoryId) qs.set('category_id', params.categoryId);
    const getUrl = `GET /api/public/trusted-search/?${qs.toString()}`;
    bodyEl.textContent = getUrl + '\n\n' + JSON.stringify(params, null, 2);
  }

  /**
   * Collect filter values (uses searchIntent from DOM via refreshIntentFromDOM).
   */
  collectFilters() {
    this.refreshIntentFromDOM();
    if (!this.searchIntent || !this.searchIntent.query || !this.searchIntent.query.trim()) {
      return null;
    }
    const params = intentToApiParams(this.searchIntent, this.currentPage);
    if (!params) return null;
    params.page = this.currentPage;
    return params;
  }

  /**
   * Handle search
   */
  async handleSearch() {
    logger.info('[TrustedFinder] handleSearch called, isSearching:', this.isSearching);
    if (this.isSearching) {
      this.pendingSearchAfterComplete = true;
      return;
    }

    const params = this.collectFilters();
    logger.info('[TrustedFinder] Collected filters:', params);
    if (!params) {
      logger.info('[TrustedFinder] No params collected, showing empty state');
      this.showEmptyState('Please enter a search term');
      return;
    }

    this.currentParams = params;
    this.currentPage = 1;
    params.page = 1;

    logger.info('[TrustedFinder] Calling performSearch with params:', params);
    await this.performSearch(params);
  }

  /**
   * Perform the actual search
   */
  async performSearch(params) {
    logger.info('[TrustedFinder] performSearch called with:', params);
    this.isSearching = true;
    this.showLoadingState(params);
    this.hideErrorState();
    this.hideEmptyState();
    this.hideResults();
    
    // Hide welcome state when search starts
    const welcome = document.getElementById('trustedWelcomeState');
    if (welcome) {
      logger.info('[TrustedFinder] Hiding welcome state');
      welcome.classList.add('hidden');
    } else {
      logger.warn('[TrustedFinder] Welcome state not found when trying to hide');
    }

    try {
      logger.info('[TrustedFinder] Calling apiService.searchTrustedSellers...');
      const response = await apiService.searchTrustedSellers(params);
      logger.info('[TrustedFinder] API response received:', response);
      
      /*
       * How the floor divided this search, taken before anything branches on
       * the results. Every state below needs it -- the results heading says
       * what cleared, and the empty state needs it most of all, because "the
       * floor hid all twenty" and "eBay matched nothing" are different pages
       * and the results list alone cannot tell them apart.
       *
       * Both spellings: handleResponse() camelCases the payload, but it does
       * not reach every nested block, and updateRateLimitPill() below has been
       * reading its own key both ways for the same reason.
       */
      this.lastFloorSplit = response.trust_floor || response.trustFloor || null;

      // Update sandbox mode indicator
      if (response.is_sandbox !== undefined) {
        this.isSandboxMode = response.is_sandbox;
        this.updateSandboxIndicator();
      }

      if (!response || !response.results) {
        this.showEmptyState('No results found');
        return;
      }

      if (response.results.length === 0) {
        this.currentResults = [];
        this.showEmptyState('No trusted sellers found for this search');
        return;
      }

      this.currentResults = response.results.slice();
      // Reset column sort when new search is performed
      this.currentSortColumn = null;
      this.currentSortDirection = 'asc';
      // Log full API response structure for debugging image issue
      if (response.results && response.results.length > 0) {
        logger.info('[TrustedFinder] Sample API response item structure:', {
          firstItem: response.results[0],
          allKeys: Object.keys(response.results[0]),
          imageFields: {
            imageUrl: response.results[0].imageUrl,
            image_url: response.results[0].image_url,
            thumbnailImages: response.results[0].thumbnailImages,
            additionalImages: response.results[0].additionalImages,
            image: response.results[0].image
          }
        });
      }
      
      this.renderResults(response.results, response);
      this.updatePagination(response);
      this.updateURL(params);
      this.showResults();

      // Scroll to results header on mobile after search completes
      // This ensures users see "Showing 1-20 of X" and first results without manual scrolling
      if (window.innerWidth <= 768) {
        const resultsHeader = document.getElementById('trustedResultsHeader');
        if (resultsHeader && !resultsHeader.classList.contains('hidden')) {
          setTimeout(() => {
            resultsHeader.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }, 100); // Small delay to ensure layout is complete
        }
      }

      // Anonymous successful-search stat (no consent; minimal payload)
      try {
        const resultsCount = response.results ? response.results.length : 0;
        if (resultsCount > 0 && window.apiService && typeof window.apiService.postAnonymousSearch === 'function') {
          const q = (params && params.q) ? String(params.q).trim() : '';
          if (q) {
            window.apiService.postAnonymousSearch({
              search_term: q,
              search_source: 'trusted_finder',
            }).catch((err) => {
              logger.warn('[TrustedFinder] Anonymous search stat failed:', err);
            });
          }
        }
      } catch (error) {
        logger.warn('[TrustedFinder] Anonymous search stat error:', error);
      }

      // Rich search analytics (non-blocking, only if user has consented)
      if (window.consentManager && window.consentManager.hasConsent('analytics')) {
        try {
          const resultsCount = response.results ? response.results.length : 0;
          if (window.apiService && typeof window.apiService.trackTrustedSearch === 'function') {
            window.apiService.trackTrustedSearch(params, resultsCount).catch(err => {
              logger.warn('[TrustedFinder] Analytics tracking failed:', err);
            });
          }
        } catch (error) {
          logger.warn('[TrustedFinder] Analytics tracking error:', error);
        }
      }

    } catch (error) {
      logger.error('[TrustedFinder] Search error:', error);
      
      // Handle rate limiting errors (429) and maintenance mode (503)
      if (error.status === 429) {
        const retrySeconds = Number(
          error.data?.retry_after ?? error.response?.retry_after ?? error.headers?.['Retry-After'] ?? 3600
        );
        const retryMinutes = Math.ceil(retrySeconds / 60);
        const errorTitle = 'Search Limit Reached';
        const errorMessage = `You've exceeded the search limit. Please try again in ${retryMinutes} minute${retryMinutes !== 1 ? 's' : ''}.`;
        this.showErrorState(errorMessage, errorTitle);
      } else if (error.status === 503) {
        const errorTitle = 'Service Temporarily Unavailable';
        // Backend may return { error: '...' } for auth/API failures (e.g. invalid_scope) or { message: '...' } for capacity
        const errorMessage = error.data?.error || error.response?.error ||
          error.data?.message || error.response?.message ||
          'We\'ve reached our daily search capacity. Our service resets at midnight UTC. Thank you for your patience!';
        this.showErrorState(errorMessage, errorTitle);
      } else {
        // Provide user-friendly error messages based on error type
        let errorMessage = 'An error occurred while searching';
        
        if (error.name === 'AbortError' || error.message?.includes('timeout')) {
          errorMessage = 'Request timed out. The search is taking too long. Please try again with different search terms.';
        } else if (error.message?.includes('network') || error.message?.includes('fetch')) {
          errorMessage = 'Network error. Please check your internet connection and try again.';
        } else if (error.message?.includes('authentication') || error.message?.includes('auth')) {
          errorMessage = 'Authentication error. Please refresh the page and try again.';
        } else if (error.message?.includes('Missing required parameter') || error.message?.includes('required')) {
          errorMessage = error.message; // Use the API's specific error message
        } else if (error.message?.includes('eBay API')) {
          // Check if there's a more detailed error message in the response
          if (error.response?.error || error.data?.error) {
            errorMessage = error.response?.error || error.data?.error; // Use the detailed error from backend
          } else {
            errorMessage = 'eBay API error. Please try again in a few moments.';
          }
        } else if (error.message) {
          errorMessage = error.message; // Use the error message if available
        } else if (error.data?.message || error.response?.message) {
          errorMessage = error.data?.message || error.response?.message;
        }
        
        // Log full error details for debugging
        logger.error('[TrustedFinder] Full error details:', {
          message: error.message,
          status: error.status,
          response: error.response,
          data: error.data
        });
        
        this.showErrorState(errorMessage);
      }
    } finally {
      this.isSearching = false;
      this.hideLoadingState();
      if (this.pendingSearchAfterComplete) {
        this.pendingSearchAfterComplete = false;
        this.handleSearch();
      }
    }
  }

  /**
   * Handle sort dropdown change: re-sort cached results in memory (no API call).
   */
  handleSortChange() {
    if (!this.currentResults || !this.currentResults.length) return;
    const sortSelect = document.getElementById('trustedSortOrder');
    const sortVal = sortSelect ? sortSelect.value : 'priceAscending';
    const validSorts = ['priceAscending', 'priceDescending', 'sellerScoreDescending', 'newlyListed', 'bestMatch'];
    const order = validSorts.includes(sortVal) ? sortVal : 'priceAscending';

    const sorted = this.currentResults.slice();
    if (order === 'priceAscending') {
      sorted.sort((a, b) => (parseFloat(a.price) || 0) - (parseFloat(b.price) || 0));
      // Update table column sort state if in details view
      if (this.currentView === 'details') {
        this.currentSortColumn = 'price';
        this.currentSortDirection = 'asc';
        this.updateColumnSortIndicators();
      }
    } else if (order === 'priceDescending') {
      sorted.sort((a, b) => (parseFloat(b.price) || 0) - (parseFloat(a.price) || 0));
      if (this.currentView === 'details') {
        this.currentSortColumn = 'price';
        this.currentSortDirection = 'desc';
        this.updateColumnSortIndicators();
      }
    } else if (order === 'sellerScoreDescending') {
      const pct = (item) => {
        const v = item.seller_feedback_pct ?? item.sellerFeedbackPct;
        return v != null ? Number(v) : -1;
      };
      sorted.sort((a, b) => pct(b) - pct(a));
      if (this.currentView === 'details') {
        this.currentSortColumn = 'feedback';
        this.currentSortDirection = 'desc';
        this.updateColumnSortIndicators();
      }
    }
    // newlyListed: API doesn't return listing date in our item shape; keep current order (no-op)

    this.currentResults = sorted;
    this.renderResults(sorted);
  }

  /**
   * Update sort dropdown to match column header sort state
   */
  updateSortDropdown() {
    const sortSelect = document.getElementById('trustedSortOrder');
    if (!sortSelect || this.currentView !== 'details') return;

    // Map column sort to dropdown value
    if (this.currentSortColumn === 'price') {
      sortSelect.value = this.currentSortDirection === 'asc' ? 'priceAscending' : 'priceDescending';
    } else if (this.currentSortColumn === 'feedback') {
      sortSelect.value = 'sellerScoreDescending';
    }
    // Note: 'title' and 'reviews' don't have dropdown equivalents, so we don't update dropdown for those
  }

  /**
   * Handle view toggle between cards and details view
   */
  /**
   * Paint the Tiles / Rows segment.
   *
   * `.on` and `aria-pressed`, which is what `.seg` is styled and announced on
   * everywhere else in the Console. This page used to carry its own
   * `.view-toggle-btn.active` pair; one component, one contract.
   */
  paintViewToggle() {
    const buttons = {
      cards: document.getElementById('trustedViewCards'),
      details: document.getElementById('trustedViewDetails'),
    };
    for (const [view, button] of Object.entries(buttons)) {
      if (!button) continue;
      const on = this.currentView === view;
      button.classList.toggle('on', on);
      button.setAttribute('aria-pressed', on ? 'true' : 'false');
    }
  }

  handleViewToggle(viewType) {
    if (this.currentView === viewType) return;
    
    this.currentView = viewType;
    
    // Store preference in localStorage
    try {
      localStorage.setItem('usedfindr_trusted_finder_view', viewType);
    } catch (e) {
      logger.warn('[TrustedFinder] Failed to save view preference:', e);
    }
    
    this.paintViewToggle();
    
    // Show/hide appropriate containers - ensure only one is visible
    const grid = document.getElementById('trustedResultsGrid');
    const table = document.getElementById('trustedResultsTable');
    const oneContainer = this.consoleResultsInOneContainer();
    if (grid) grid.classList.toggle('hidden', !oneContainer && viewType !== 'cards');
    if (table) table.classList.toggle('hidden', oneContainer || viewType !== 'details');
    
    // Re-render results using cached data (no API call)
    if (this.currentResults && this.currentResults.length > 0) {
      this.renderResults(this.currentResults);
    }
  }

  /**
   * Handle column sorting in table view
   */
  handleColumnSort(columnName) {
    if (!this.currentResults || !this.currentResults.length) return;
    
    // Toggle direction if same column, otherwise set to ascending
    if (this.currentSortColumn === columnName) {
      this.currentSortDirection = this.currentSortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.currentSortColumn = columnName;
      this.currentSortDirection = 'asc';
    }
    
    // Sort cached results
    const sorted = this.currentResults.slice();
    
    switch (columnName) {
      case 'price':
        if (this.currentSortDirection === 'asc') {
          sorted.sort((a, b) => (parseFloat(a.price) || 0) - (parseFloat(b.price) || 0));
        } else {
          sorted.sort((a, b) => (parseFloat(b.price) || 0) - (parseFloat(a.price) || 0));
        }
        break;
      case 'feedback':
        {
          const pct = (item) => {
            const v = item.seller_feedback_pct ?? item.sellerFeedbackPct;
            return v != null ? Number(v) : -1;
          };
          if (this.currentSortDirection === 'asc') {
            sorted.sort((a, b) => pct(a) - pct(b));
          } else {
            sorted.sort((a, b) => pct(b) - pct(a));
          }
        }
        break;
      case 'reviews':
        {
          const reviews = (item) => {
            const v = item.seller_sales_count ?? item.sellerSalesCount;
            return v != null ? Number(v) : -1;
          };
          if (this.currentSortDirection === 'asc') {
            sorted.sort((a, b) => reviews(a) - reviews(b));
          } else {
            sorted.sort((a, b) => reviews(b) - reviews(a));
          }
        }
        break;
      case 'title':
        {
          const title = (item) => (item.title || item.name || '').toLowerCase();
          if (this.currentSortDirection === 'asc') {
            sorted.sort((a, b) => title(a).localeCompare(title(b)));
          } else {
            sorted.sort((a, b) => title(b).localeCompare(title(a)));
          }
        }
        break;
    }
    
    this.currentResults = sorted;
    this.updateColumnSortIndicators();
    this.renderResults(sorted);
  }

  /**
   * Update column sort indicators in table headers
   */
  updateColumnSortIndicators() {
    const headers = document.querySelectorAll('.trusted-results-table th.sortable');
    headers.forEach(header => {
      const columnName = header.getAttribute('data-sort');
      header.classList.remove('active', 'sort-asc', 'sort-desc');
      if (columnName === this.currentSortColumn) {
        header.classList.add('active', `sort-${this.currentSortDirection}`);
      }
    });
    
    // Also update sort dropdown if in details view
    if (this.currentView === 'details') {
      this.updateSortDropdown();
    }
  }

  /**
   * Render search results (cards or table view based on currentView)
   * @param {Array} items - Array of result items
   * @param {Object} response - Full API response object (optional, for count info)
   */
  renderResults(items, response = null) {
    if (this.currentView === 'cards') {
      this.renderCardsView(items);
    } else {
      this.renderDetailsView(items);
    }

    this.renderResultsHeading(items, response);

    // Update rate limit pill (X of Y searches left this hour)
    this.updateRateLimitPill(response);
  }

  /**
   * The heading and status line over a page of results.
   *
   * "7 clear Strict", then what that cost: how many listings eBay matched and
   * how many the floor withheld. Both numbers are measured rather than
   * inferred — the results list has already had the failures removed from it,
   * so the endpoint counts them on the way past and returns a `trust_floor`
   * block (see TrustedSearchView). Without that block the line says only what
   * it can support, which is the count and the sort.
   */
  renderResultsHeading(items, response = null) {
    const titleEl = document.getElementById('trustedResultsTitle');
    const countEl = document.getElementById('trustedResultsCount');
    const split = (response && (response.trust_floor || response.trustFloor))
      || this.lastFloorSplit
      || null;
    this.lastFloorSplit = split;

    const preset = this.activePreset();
    const shown = items.length;
    const floorOn = !!(split && split.checked);

    if (titleEl) {
      titleEl.textContent = floorOn
        ? `${shown} clear ${preset.label}`
        : `${shown} result${shown === 1 ? '' : 's'}`;
    }
    if (!countEl) return;

    const parts = [];
    if (split && split.matched) {
      const hidden = (split.below || 0) + (split.unrated || 0);
      parts.push(`<span><b>${split.matched}</b> matched on eBay${
        floorOn ? ` · <b>${hidden}</b> hidden below the floor` : ''}</span>`);
    } else {
      const pageSize = 20;
      const total = response?.count || shown;
      const start = (this.currentPage - 1) * pageSize + 1;
      const end = Math.min(this.currentPage * pageSize, total);
      parts.push(`<span>Showing <b>${start}-${end}</b> of ${total}</span>`);
    }
    parts.push(`<span>${this.sortSentence()}</span>`);
    countEl.innerHTML = parts.join('');
  }

  /** How the current result set is ordered, in words. */
  sortSentence() {
    const select = document.getElementById('trustedSortOrder');
    const value = select ? select.value : 'sellerScoreDescending';
    const sentences = {
      sellerScoreDescending: 'sorted by trust score, then price',
      priceAscending: 'sorted by price, lowest first',
      priceDescending: 'sorted by price, highest first',
      newlyListed: 'sorted by newest listing first',
      bestMatch: 'sorted by best match',
    };
    return sentences[value] || sentences.sellerScoreDescending;
  }

  /**
   * Update the rate limit pill from API response (rate_limit or rateLimit with limit/remaining).
   * @param {Object} response - API response object
   */
  updateRateLimitPill(response = null) {
    const pill = document.getElementById('trustedRateLimitPill');
    // The Console's guest-limits panel states the same figure. A second
    // element with the same id would be two answers to one question, so it is
    // an attribute and this method fills both.
    const mirrors = [...document.querySelectorAll('[data-rate-limit]')];
    const mirror = (text) => { for (const node of mirrors) node.textContent = text; };
    if (!pill && !mirrors.length) return;
    const rl = response?.rate_limit || response?.rateLimit;
    if (!rl || rl.limit == null) {
      mirror('—');
      if (!pill) return;
      pill.classList.add('hidden');
      pill.textContent = '';
      return;
    }
    const remaining = typeof rl.remaining === 'number' ? rl.remaining : parseInt(rl.remaining, 10);
    const limit = typeof rl.limit === 'number' ? rl.limit : parseInt(rl.limit, 10);
    mirror(`${remaining} / ${limit}`);
    if (!pill) return;
    pill.classList.remove('hidden');
    pill.classList.remove('rate-limit-low', 'rate-limit-none');
    if (remaining <= 0) {
      pill.classList.add('rate-limit-none');
      pill.textContent = 'No searches left this hour. Try again later.';
    } else {
      if (remaining <= 2) pill.classList.add('rate-limit-low');
      pill.textContent = `${remaining} of ${limit} searches left this hour`;
    }
  }

  /**
   * Render cards view
   */
  renderCardsView(items) {
    const grid = document.getElementById('trustedResultsGrid');
    if (!grid) return;

    if (document.body && document.body.classList.contains('console')) {
      this.renderConsoleResults(grid, items, 'tiles');
      return;
    }

    grid.innerHTML = '';

    items.forEach(item => {
      const card = this.renderTrustedProductCard(item);
      grid.appendChild(card);
    });
  }

  /**
   * Render details/table view
   */
  renderDetailsView(items) {
    // Under the Console the details view is the Rows component -- the same
    // ledger the boards call for, and the same one the feed and search draw.
    // The legacy <table> stays for the legacy theme.
    if (document.body && document.body.classList.contains('console')) {
      const grid = document.getElementById('trustedResultsGrid');
      if (grid) this.renderConsoleResults(grid, items, 'rows');
      return;
    }

    const tableBody = document.getElementById('trustedResultsTableBody');
    if (!tableBody) return;

    tableBody.innerHTML = '';

    items.forEach(item => {
      const row = this.renderTrustedProductTableRow(item);
      tableBody.appendChild(row);
    });
  }

  /**
   * Render a table row for an item
   */
  renderTrustedProductTableRow(item) {
    const row = document.createElement('tr');
    
    // Format price
    const price = item.price ? `$${parseFloat(item.price).toFixed(2)}` : 'N/A';
    
    // Seller stats
    const feedbackPct = item.sellerFeedbackPct || item.seller_feedback_pct;
    const salesCount = item.sellerSalesCount || item.seller_sales_count;
    const feedbackDisplay = feedbackPct ? `${feedbackPct.toFixed(1)}%` : 'N/A';
    const reviewsDisplay = salesCount ? salesCount.toLocaleString() : 'N/A';
    
    // Image extraction per eBay Browse API official structure (same as card view)
    // Priority: additionalImages[0].imageUrl > image.imageUrl > imageUrl/image_url > thumbnailImages
    let imageUrl = '';
    let imageSource = '';
    
    // Strategy 1: Try additionalImages[0].imageUrl first (often highest quality)
    if (item.additionalImages && Array.isArray(item.additionalImages) && item.additionalImages.length > 0) {
      const firstAdditional = item.additionalImages[0];
      if (typeof firstAdditional === 'object' && firstAdditional !== null) {
        imageUrl = firstAdditional.imageUrl || firstAdditional.image_url || '';
      } else if (typeof firstAdditional === 'string') {
        imageUrl = firstAdditional;
      }
      if (imageUrl) imageSource = 'additionalImages[0].imageUrl';
    }
    
    // Strategy 2: Fall back to primary image.imageUrl
    if (!imageUrl) {
      const imageData = item.image || {};
      if (typeof imageData === 'object' && imageData !== null) {
        imageUrl = imageData.imageUrl || imageData.image_url || '';
      }
      if (imageUrl) imageSource = 'image.imageUrl';
    }
    
    // Strategy 3: Try direct imageUrl/image_url fields
    if (!imageUrl) {
      imageUrl = item.imageUrl || item.image_url || '';
      if (imageUrl) imageSource = 'imageUrl/image_url';
    }
    
    // Strategy 4: Last resort - thumbnailImages
    if (!imageUrl && item.thumbnailImages && Array.isArray(item.thumbnailImages) && item.thumbnailImages.length > 0) {
      const firstThumb = item.thumbnailImages[0];
      if (typeof firstThumb === 'object' && firstThumb !== null) {
        imageUrl = firstThumb.imageUrl || firstThumb.image_url || '';
      } else if (typeof firstThumb === 'string') {
        imageUrl = firstThumb;
      }
      if (imageUrl) imageSource = 'thumbnailImages';
    }
    
    // Upgrade image URL to highest resolution available
    imageUrl = this.getHighResUrl(imageUrl);
    
    const imageSrc = imageUrl || 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMzAwIiBoZWlnaHQ9IjMwMCIgZmlsbD0iI2Y1ZjVmNSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTgiIGZpbGw9IiM5OTk5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5ObyBJbWFnZTwvdGV4dD48L3N2Zz4=';
    
    // Debug logging
    if (imageUrl) {
      logger.info(`[TrustedFinder] Table image from ${imageSource}:`, imageUrl);
    }
    
    // eBay link
    const ebayUrl = item.itemWebUrl || item.item_web_url || '#';
    
    // Title
    const title = item.title || item.name || 'Untitled Product';
    
    // Condition
    const condition = item.condition || 'N/A';

    // Shipping cost (eBay: Free, $X.XX, or Calculated)
    const shippingCost = item.shipping_cost ?? item.shippingCost;
    let shippingDisplay = '';
    if (shippingCost === 'Free') {
      shippingDisplay = '<span class="trusted-shipping-badge">Free</span>';
    } else if (shippingCost === 'Calculated') {
      shippingDisplay = 'Calculated';
    } else if (shippingCost) {
      shippingDisplay = `+${this.escapeHtml(shippingCost)}`;
    }

    // Quantity available - only when min filter >= 2
    const minQuantity = this.currentParams.minQuantity ?? 1;
    const qtyAvailable = item.quantity_available ?? item.quantityAvailable;
    const quantityDisplay = minQuantity >= 2 && typeof qtyAvailable === 'number' && !Number.isNaN(qtyAvailable) && qtyAvailable >= 0
      ? String(qtyAvailable)
      : '';

    // Store full item data on the row for submission
    row.setAttribute('data-item-data', JSON.stringify(item));

    row.innerHTML = `
      <td class="col-image">
        <img src="${this.escapeHtml(imageSrc)}" alt="${this.escapeHtml(title)}" loading="lazy">
      </td>
      <td class="col-title">
        <a href="${this.escapeHtml(ebayUrl)}" target="_blank" rel="noopener noreferrer">${this.escapeHtml(title)}</a>
      </td>
      <td class="col-price">${this.escapeHtml(price)}</td>
      <td class="col-feedback">
        <div class="tf-trust tf-trust--inline" data-tier="${this.sellerTrustTier(feedbackPct, salesCount)}">
          <span class="tf-trust__meter" role="img" aria-label="Seller trust tier ${this.sellerTrustTier(feedbackPct, salesCount)} of 3"><i></i><i></i><i></i></span>
          <span class="tf-trust__num">${this.escapeHtml(feedbackDisplay)}</span>
        </div>
      </td>
      <td class="col-reviews">${this.escapeHtml(reviewsDisplay)}</td>
      <td class="col-condition">${this.escapeHtml(condition)}</td>
      <td class="col-shipping">${shippingDisplay}</td>
      <td class="col-quantity">${this.escapeHtml(quantityDisplay)}</td>
      <td class="col-actions">
        <a href="${this.escapeHtml(ebayUrl)}" target="_blank" rel="noopener noreferrer" class="buy-button" data-ebay-url="${this.escapeHtml(ebayUrl)}">
          View on eBay
        </a>
      </td>
    `;

    // Add error handler for image loading
    const img = row.querySelector('.col-image img');
    if (img) {
      img.addEventListener('error', function() {
        this.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMzAwIiBoZWlnaHQ9IjMwMCIgZmlsbD0iI2Y1ZjVmNSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTgiIGZpbGw9IiM5OTk5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5ObyBJbWFnZTwvdGV4dD48L3N2Zz4=';
      });
    }

    // Click handler for "View on eBay" button - intercept and submit
    const buyButton = row.querySelector('.buy-button');
    if (buyButton) {
      buyButton.addEventListener('click', async (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        // Extract item data
        let itemData = null;
        const itemDataStr = row.getAttribute('data-item-data');
        if (itemDataStr) {
          try {
            itemData = JSON.parse(itemDataStr);
            // Submit to admin review queue
            await this.submitTrustedFinderItem(itemData);
          } catch (error) {
            logger.error('[TrustedFinder] Error parsing item data:', error);
          }
        }
        
        // Open eBay link after submission
        const ebayLink = buyButton.getAttribute('data-ebay-url') || ebayUrl;
        if (ebayLink && ebayLink !== '#') {
          this.openExternalLink(
            ebayLink,
            this.outboundTrackOptionsFromItem(itemData || item)
          );
        }
      });
    }

    return row;
  }

  /**
   * Mobile: present the filters panel as a bottom sheet.
   *
   * The panel keeps its place in the DOM — only its presentation changes — so
   * the dual price slider, dirty tracking and preset sync all keep working
   * untouched. Above 800px none of this applies and the panel stays inline.
   */
  setupFiltersSheet() {
    const openBtn = document.getElementById('tfFiltersSheetOpen');
    const closeBtn = document.getElementById('tfFiltersSheetClose');
    const applyBtn = document.getElementById('tfFiltersSheetApply');
    const scrim = document.getElementById('tfFiltersSheetScrim');
    /*
     * The sheet is the pane.
     *
     * Below 1100 the pane is not rendered as a column, and the floor and
     * refine controls are the page's whole control surface. The alternative --
     * a second copy of those controls inside a mobile panel -- is what this
     * page used to do, and it meant two elements with one id and two sets of
     * handlers for every filter. Opening the real pane over the content keeps
     * one control per job at every width.
     */
    const panel = document.querySelector('.trusted-finder-page .pane');
    if (!openBtn || !panel || openBtn.dataset.listenerAttached === 'true') return;
    openBtn.dataset.listenerAttached = 'true';

    let lastFocused = null;

    const open = () => {
      lastFocused = document.activeElement;
      document.body.classList.add('tf-sheet-open');
      openBtn.setAttribute('aria-expanded', 'true');
      if (scrim) scrim.hidden = false;
      // Focus into the sheet, on the first thing in it that can take focus --
      // the floor selector's active radio. Leaving focus on the trigger behind
      // the scrim would put the next Tab somewhere the visitor cannot see.
      const first = panel.querySelector('[data-floorsel] [tabindex="0"]')
        || panel.querySelector('button, input, select, [tabindex="0"]');
      (closeBtn || first || panel).focus?.();
    };

    const close = () => {
      document.body.classList.remove('tf-sheet-open');
      openBtn.setAttribute('aria-expanded', 'false');
      if (scrim) scrim.hidden = true;
      // Focus must land somewhere deliberate: whatever opened the sheet, or the
      // trigger. Falling back to <body> would strand keyboard users at the top
      // of the document after the sheet they were working in disappears.
      const target = lastFocused && lastFocused !== document.body && typeof lastFocused.focus === 'function'
        ? lastFocused
        : openBtn;
      target.focus();
      lastFocused = null;
    };

    openBtn.addEventListener('click', open);
    closeBtn?.addEventListener('click', close);
    scrim?.addEventListener('click', close);
    applyBtn?.addEventListener('click', () => {
      close();
      // Re-run with whatever the sheet just changed, but only if there is a
      // query. handleSearch() collects the filters first; performSearch() takes
      // already-collected params and would search on undefined.
      const input = document.getElementById('trustedSearchInput');
      if (input?.value?.trim()) this.handleSearch();
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      if (!document.body.classList.contains('tf-sheet-open')) return;
      close();
    });

    // Growing past the breakpoint with the sheet open would strand
    // `overflow: hidden` on the body while the pane snapped back to being a
    // column. 1100 because that is where shell.css takes the pane away, and
    // this sheet exists only below it.
    window.matchMedia('(max-width: 1100px)').addEventListener('change', (event) => {
      if (!event.matches) close();
    });
  }

  /**
   * How far past the floor this seller sits, as a 0–3 tier:
   *   3 clears Elite, 2 clears Strict, 1 clears Standard, 0 clears none.
   *
   * The thresholds are the preset table, so the meter answers a question the old
   * flat "Trusted" badge could not: a 100% / 93,000-review seller and a
   * 99.5% / 60-review seller both passed the floor, but not by the same margin.
   */
  sellerTrustTier(feedbackPct, reviewCount) {
    const pct = Number(feedbackPct);
    const reviews = Number(reviewCount);
    if (Number.isNaN(pct) || Number.isNaN(reviews)) return 0;
    const clears = (level) => pct >= level.feedback && reviews >= level.reviews;
    if (clears(TRUSTED_PRESETS.elite)) return 3;
    if (clears(TRUSTED_PRESETS.strict)) return 2;
    if (clears(TRUSTED_PRESETS.standard)) return 1;
    return 0;
  }

  /**
   * The preset actually in force right now — what the badge names instead of a
   * flat "Trusted". Mirrors updatePresetBadge()'s reading of the controls.
   */
  activePreset() {
    const select = document.getElementById('trustedLevelSelect');
    if (select && select.disabled) return { key: 'custom', label: 'Custom' };
    const key = select && FLOOR_NAMES[select.value] ? select.value : 'strict';
    return { key, label: FLOOR_NAMES[key] };
  }

  /**
   * Make the trust floor this page's preset control.
   *
   * v3 deletes the page's own "Trusted level" dropdown: the preset a visitor
   * picks here is the same floor the top bar carries and the same one that
   * filters the feed, search and hubs, so there is one control for it and it
   * lives in floor-state.js. `#trustedLevelSelect` stays in the DOM because
   * domToIntent() reads it, but it is now an output — this writes it.
   *
   * Deliberately not the reverse: nothing on this page writes the select
   * directly any more, so there is no path where the two can disagree.
   */
  syncFloorToPreset(floor = getFloor(), { rerun = false } = {}) {
    const select = document.getElementById('trustedLevelSelect');
    if (!select || !this.trustedLevels[floor]) return;

    // The page's one-line promise, painted before the early return below: at
    // Off nothing is checked, and leaving the sentence up would advertise a
    // guarantee the visitor has just switched off. It is the one claim on this
    // page that has to track the control rather than the search.
    const line = document.querySelector('[data-tf-floor-line]');
    if (line) {
      // The clause before this one already says "every seller shown", so the
      // Off wording has to add something rather than agree: what changes at
      // Off is that nothing is withheld, including the sellers we hold no
      // numbers for, and that is the part a visitor is choosing between.
      line.textContent = floor === 'off'
        ? 'nothing is filtered out before you see it, unrated sellers included'
        : 'every result checked against the floor before you see it';
    }

    if (select.value === floor) return;
    select.value = floor;

    // The custom feedback/review pair is expressed relative to a preset, so a
    // new preset makes the old pair meaningless rather than merely stale.
    this.syncPresetToInlineDisplays();
    this.syncSellerCriteriaUi();
    this.updatePresetBadge();

    if (rerun && this.currentParams.q) this.handleSearch();
    else this.renderFloorSample();
  }

  /**
   * The empty state's demonstration rows.
   *
   * Nothing has been searched yet, so rather than an essay explaining what the
   * floor is for, the page shows what it is letting through right now. Same
   * catalog endpoint the feed uses, at the active floor — so this is not a
   * mock-up of the promise, it is the promise being kept.
   *
   * Failure here is silent by design: this is an illustration under a search
   * box that works with or without it, and an error panel in its place would
   * make a decorative fetch look like a broken page.
   */
  async renderFloorSample() {
    const slot = document.querySelector('[data-tf-sample]');
    if (!slot) return;
    const floor = getFloor();
    const token = Symbol('sample');
    this._sampleToken = token;
    try {
      const items = await apiService.getProducts({
        status: 'active', limit: 6, ordering: '-created_at', floor,
      });
      // A newer floor change (or a real search) started after this one; its
      // answer is the current one and this response is already history.
      if (this._sampleToken !== token) return;
      const cards = (items || []).slice(0, 6)
        .map((item) => {
          const image = this.trustedImageUrl(item);
          if (!/^https?:\/\//.test(String(image))) return null;
          return toCard(item, { url: item.url || '#', imageUrl: image });
        })
        .filter(Boolean);
      slot.innerHTML = cards.length ? consoleRows(cards) : '';
    } catch (error) {
      if (this._sampleToken !== token) return;
      logger.warn('[TrustedFinder] Could not draw the floor sample', error);
      slot.innerHTML = '';
    }
  }

  /**
   * Trust block for a result card: tier meter + the seller's own numbers in
   * tabular figures + the active preset badge.
   */
  renderTrustBlock(feedbackPct, reviewCount, tier) {
    const preset = this.activePreset();
    const hasNumbers = !Number.isNaN(Number(feedbackPct)) && !Number.isNaN(Number(reviewCount));
    const numbers = hasNumbers
      ? `<span class="tf-trust__num"><b>${Number(feedbackPct).toFixed(1)}%</b> · ${Number(reviewCount).toLocaleString()} reviews</span>`
      : '<span class="tf-trust__num">Seller stats unavailable</span>';
    const tierLabels = {
      0: 'below the Standard floor',
      1: 'clears Standard',
      2: 'clears Strict',
      3: 'clears Elite',
    };
    return `
      <div class="tf-trust" data-tier="${tier}">
        <span class="tf-trust__meter" role="img" aria-label="Seller trust tier ${tier} of 3 — ${tierLabels[tier]}"><i></i><i></i><i></i></span>
        ${numbers}
        <span class="tf-trust__badge" data-preset="${preset.key}"><i aria-hidden="true"></i>${this.escapeHtml(preset.label)}</span>
      </div>`;
  }

  /**
   * Render a trusted product card with seller badge
   */
  /**
   * The best image eBay gave us for this item, upgraded to the largest size.
   *
   * Pulled out of renderTrustedProductCard() so the Console renderer can use
   * the same four fallbacks. A Browse result puts its photo in one of four
   * places depending on the listing, and picking the wrong one is the
   * difference between a photo and a grey box.
   */
  trustedImageUrl(item) {
    let imageUrl = '';

    const additional = item.additionalImages;
    if (Array.isArray(additional) && additional.length) {
      const first = additional[0];
      imageUrl = typeof first === 'string'
        ? first
        : (first && (first.imageUrl || first.image_url)) || '';
    }
    if (!imageUrl) {
      const image = item.image || {};
      if (typeof image === 'object' && image !== null) {
        imageUrl = image.imageUrl || image.image_url || '';
      }
    }
    if (!imageUrl) imageUrl = item.imageUrl || item.image_url || '';
    if (!imageUrl && Array.isArray(item.thumbnailImages) && item.thumbnailImages.length) {
      const first = item.thumbnailImages[0];
      imageUrl = typeof first === 'string'
        ? first
        : (first && (first.imageUrl || first.image_url)) || '';
    }
    return this.getHighResUrl(imageUrl);
  }

  /**
   * Draw a page of live eBay results as Console tiles or rows.
   *
   * The same components the feed, search and hubs use — a listing looks the
   * same wherever you meet it. Two things differ and both are props: the link
   * goes off-platform (these listings have no page on this site yet), and the
   * item behind each card is attached as a property rather than serialised
   * into a data attribute, so one delegated handler can do what the per-card
   * handlers used to.
   */
  renderConsoleResults(container, items, view) {
    const cards = items
      .map((item) => {
        const image = this.trustedImageUrl(item);
        if (!/^https?:\/\//.test(String(image))) return null;
        return {
          item,
          card: toCard(item, {
            url: item.itemWebUrl || item.item_web_url || '#',
            imageUrl: image,
            // A Browse-API item names this `title`. Passed explicitly rather
            // than left to toCard's fallback so the mapping is visible at the
            // one place that holds eBay's field names -- the same reason `url`
            // and `imageUrl` are passed rather than inferred.
            name: item.title || item.name || '',
            external: true,
          }),
        };
      })
      .filter(Boolean);

    container.innerHTML = view === 'rows'
      ? consoleRows(cards.map((entry) => entry.card))
      : consoleGrid(cards.map((entry) => entry.card));

    // Attach the source item to each anchor, in the order they were drawn.
    const anchors = container.querySelectorAll('.tile, .rows .row:not(.h)');
    anchors.forEach((anchor, index) => {
      if (cards[index]) anchor._trustedItem = cards[index].item;
    });
    this.bindConsoleResultClicks(container);
  }

  /**
   * One delegated click handler per container.
   *
   * Clicking a result still submits it to the admin review queue and still
   * goes out through the outbound tracker — that is how a live eBay listing
   * becomes a catalog row. The components render as strings, so the handler
   * lives on the container rather than on each card.
   */
  bindConsoleResultClicks(container) {
    if (container.dataset.consoleClicks === '1') return;
    container.dataset.consoleClicks = '1';
    container.addEventListener('click', async (event) => {
      const anchor = event.target.closest('.tile, .row');
      if (!anchor || !container.contains(anchor)) return;
      const item = anchor._trustedItem;
      if (!item) return;
      event.preventDefault();
      try {
        await this.submitTrustedFinderItem(item);
      } catch (error) {
        logger.error('[TrustedFinder] Could not queue the item', error);
      }
      const href = anchor.getAttribute('href');
      if (href && href !== '#') {
        this.openExternalLink(href, this.outboundTrackOptionsFromItem(item));
      }
    });
  }

  /**
   * Which container holds the results.
   *
   * Under the Console both views are drawn into #trustedResultsGrid -- Rows is
   * a component, not a <table> -- so the table container stays hidden and the
   * grid stays visible whichever view is on. The legacy theme keeps one
   * container per view.
   */
  consoleResultsInOneContainer() {
    return !!(document.body && document.body.classList.contains('console'));
  }

  renderTrustedProductCard(item) {
    const card = document.createElement('div');
    card.className = 'product-card trusted-product-card';
    card.style.cursor = 'pointer';

    // Format price
    const price = item.price ? `$${parseFloat(item.price).toFixed(2)}` : 'N/A';
    
    // Seller stats — rendered by renderTrustBlock() below the price.
    const feedbackPct = item.sellerFeedbackPct ?? item.seller_feedback_pct;
    const salesCount = item.sellerSalesCount ?? item.seller_sales_count;
    const pct = Number(feedbackPct);
    const reviews = Number(salesCount);
    const tier = this.sellerTrustTier(pct, reviews);

    // Image extraction per eBay Browse API official structure:
    // Priority: additionalImages[0].imageUrl > image.imageUrl > imageUrl/image_url > thumbnailImages
    // - additionalImages[0].imageUrl: Often highest quality product photos
    // - image.imageUrl: Primary image (Image object with imageUrl field)
    // - imageUrl/image_url: Direct fields (backward compatibility)
    // - thumbnailImages: Array of Image objects (thumbnails, lowest quality)
    
    let imageUrl = '';
    let imageSource = '';
    
    // Strategy 1: Try additionalImages[0].imageUrl first (often highest quality product photos)
    if (item.additionalImages && Array.isArray(item.additionalImages) && item.additionalImages.length > 0) {
      const firstAdditional = item.additionalImages[0];
      if (typeof firstAdditional === 'object' && firstAdditional !== null) {
        imageUrl = firstAdditional.imageUrl || firstAdditional.image_url || '';
      } else if (typeof firstAdditional === 'string') {
        imageUrl = firstAdditional;
      }
      if (imageUrl) imageSource = 'additionalImages[0].imageUrl';
    }
    
    // Strategy 2: Fall back to primary image.imageUrl
    if (!imageUrl) {
      const imageData = item.image || {};
      if (typeof imageData === 'object' && imageData !== null) {
        imageUrl = imageData.imageUrl || imageData.image_url || '';
      }
      if (imageUrl) imageSource = 'image.imageUrl';
    }
    
    // Strategy 3: Try direct imageUrl/image_url fields (backward compatibility)
    if (!imageUrl) {
      imageUrl = item.imageUrl || item.image_url || '';
      if (imageUrl) imageSource = 'imageUrl/image_url';
    }
    
    // Strategy 4: Last resort - thumbnailImages (lowest quality)
    if (!imageUrl && item.thumbnailImages && Array.isArray(item.thumbnailImages) && item.thumbnailImages.length > 0) {
      const firstThumb = item.thumbnailImages[0];
      if (typeof firstThumb === 'object' && firstThumb !== null) {
        imageUrl = firstThumb.imageUrl || firstThumb.image_url || '';
      } else if (typeof firstThumb === 'string') {
        imageUrl = firstThumb;
      }
      if (imageUrl) imageSource = 'thumbnailImages';
    }
    
    // Upgrade image URL to highest resolution available
    imageUrl = this.getHighResUrl(imageUrl);
    
    const imageSrc = imageUrl || 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMzAwIiBoZWlnaHQ9IjMwMCIgZmlsbD0iI2Y1ZjVmNSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTgiIGZpbGw9IiM5OTk5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5ObyBJbWFnZTwvdGV4dD48L3N2Zz4=';
    
    // Debug logging
    if (imageUrl) {
      logger.info(`[TrustedFinder] Card image from ${imageSource}:`, imageUrl, 'for item:', item.title || item.name);
    } else {
      logger.warn('[TrustedFinder] No image URL found. Available fields:', {
        hasImage: !!item.image,
        hasAdditionalImages: !!(item.additionalImages && item.additionalImages.length),
        hasThumbnailImages: !!(item.thumbnailImages && item.thumbnailImages.length),
        hasImageUrl: !!item.imageUrl,
        allKeys: Object.keys(item)
      });
    }
    
    // eBay link
    const ebayUrl = item.itemWebUrl || item.item_web_url || '#';
    
    // Title
    const title = item.title || item.name || 'Untitled Product';

    // Condition: eBay returns e.g. "NEW", "USED" – title-case for display
    const conditionRaw = item.condition ?? item.condition_id;
    const conditionDisplay = conditionRaw != null && String(conditionRaw).trim()
      ? String(conditionRaw).trim().replace(/\b\w/g, c => c.toUpperCase())
      : '';

    // Category: name preferred, fallback to ID
    const categoryName = item.ebay_category_name ?? item.ebayCategoryName;
    const categoryId = item.ebay_category_id ?? item.ebayCategoryId;
    const categoryDisplay = (categoryName && String(categoryName).trim()) || (categoryId != null && String(categoryId).trim())
      ? `Category: ${categoryName ? this.escapeHtml(String(categoryName).trim()) : this.escapeHtml(String(categoryId).trim())}`
      : '';

    // Availability: "Ends in X hours/days" or local date/time
    const endTimeRaw = item.item_end_time ?? item.itemEndTime;
    const availabilityDisplay = this.formatItemEndTime(endTimeRaw);

    // Shipping cost (eBay: Free, $X.XX, or Calculated)
    const shippingCost = item.shipping_cost ?? item.shippingCost;
    const isFreeShipping = shippingCost === 'Free';
    const freeShippingBadgeHtml = isFreeShipping
      ? '<span class="trusted-shipping-badge">Free shipping</span>'
      : '';

    // Quantity available (eBay estimatedAvailabilities) - only when min filter >= 2
    const minQuantity = this.currentParams.minQuantity ?? 1;
    const qtyAvailable = item.quantity_available ?? item.quantityAvailable;
    const quantityDisplay = minQuantity >= 2 && typeof qtyAvailable === 'number' && !Number.isNaN(qtyAvailable) && qtyAvailable >= 0
      ? `${qtyAvailable} available`
      : '';

    // Row 1 (trust): seller-stats + shipping (when not free)
    // Seller stats moved into the trust block below the price; repeating them
    // here would state the same two numbers twice on one card.
    const trustRowItems = [];
    if (shippingCost && shippingCost !== 'Free') {
      if (shippingCost === 'Calculated') {
        trustRowItems.push('<span class="trusted-card-meta trusted-card-shipping">Calculated shipping</span>');
      } else {
        trustRowItems.push(`<span class="trusted-card-meta trusted-card-shipping">+ ${this.escapeHtml(shippingCost)} shipping</span>`);
      }
    }
    const trustRowHtml = trustRowItems.length > 0
      ? `<div class="product-metadata-row product-metadata-row--trust">${trustRowItems.join('')}</div>`
      : '';

    // Row 2 (pills): condition, category, quantity, availability
    const pillsRowItems = [];
    if (conditionDisplay) {
      pillsRowItems.push(`<span class="metadata-pill">Condition: ${this.escapeHtml(conditionDisplay)}</span>`);
    }
    if (categoryDisplay) {
      pillsRowItems.push(`<span class="metadata-pill">${categoryDisplay}</span>`);
    }
    if (quantityDisplay) {
      pillsRowItems.push(`<span class="metadata-pill">${this.escapeHtml(quantityDisplay)}</span>`);
    }
    if (availabilityDisplay) {
      pillsRowItems.push(`<span class="metadata-pill">${this.escapeHtml(availabilityDisplay)}</span>`);
    }
    const pillsRowHtml = pillsRowItems.length > 0
      ? `<div class="product-metadata-row product-metadata-row--pills">${pillsRowItems.join('')}</div>`
      : '';

    const trustedMetadataHtml = (trustRowHtml || pillsRowHtml)
      ? `<div class="product-metadata">${trustRowHtml}${pillsRowHtml}</div>`
      : '';

    // Store full item data on the card for submission
    card.setAttribute('data-item-data', JSON.stringify(item));

    card.innerHTML = `
      <div class="card-content">
        <div class="product-image">
          <img src="${this.escapeHtml(imageSrc)}" alt="${this.escapeHtml(title)}" loading="lazy">
          ${freeShippingBadgeHtml}
        </div>
        <div class="product-info">
          <h3 class="product-title">${this.escapeHtml(title)}</h3>
          <div class="product-price">${this.escapeHtml(price)}</div>
          ${trustedMetadataHtml}
          ${this.renderTrustBlock(pct, reviews, tier)}
        </div>
      </div>
      <div class="card-footer-actions">
        <div class="product-card-actions">
          <a href="${this.escapeHtml(ebayUrl)}" target="_blank" rel="noopener noreferrer" class="buy-button" data-ebay-url="${this.escapeHtml(ebayUrl)}">
            View on eBay
          </a>
        </div>
      </div>
    `;

    // Add error handler for image loading
    const img = card.querySelector('.product-image img');
    if (img) {
      img.addEventListener('error', function() {
        this.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMzAwIiBoZWlnaHQ9IjMwMCIgZmlsbD0iI2Y1ZjVmNSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTgiIGZpbGw9IiM5OTk5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5ObyBJbWFnZTwvdGV4dD48L3N2Zz4=';
      });
    }

    // Click handler for "View on eBay" button - intercept and submit
    const buyButton = card.querySelector('.buy-button');
    if (buyButton) {
      buyButton.addEventListener('click', async (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        // Extract item data
        let itemData = null;
        const itemDataStr = card.getAttribute('data-item-data');
        if (itemDataStr) {
          try {
            itemData = JSON.parse(itemDataStr);
            // Submit to admin review queue
            await this.submitTrustedFinderItem(itemData);
          } catch (error) {
            logger.error('[TrustedFinder] Error parsing item data:', error);
          }
        }
        
        // Open eBay link after submission
        const ebayLink = buyButton.getAttribute('data-ebay-url') || ebayUrl;
        if (ebayLink && ebayLink !== '#') {
          this.openExternalLink(
            ebayLink,
            this.outboundTrackOptionsFromItem(itemData || item)
          );
        }
      });
    }

    // Click handler for card body (not "View on eBay" button or vote)
    card.addEventListener('click', async (e) => {
      if (e.target.closest('.buy-button') || e.target.closest('.vote-container')) return;

      let itemData = null;
      const itemDataStr = card.getAttribute('data-item-data');
      if (itemDataStr) {
        try {
          itemData = JSON.parse(itemDataStr);
          await this.submitTrustedFinderItem(itemData);
        } catch (error) {
          logger.error('[TrustedFinder] Error parsing item data on card click:', error);
        }
      }

      this.openExternalLink(
        ebayUrl,
        this.outboundTrackOptionsFromItem(itemData || item)
      );
    });

    return card;
  }

  /**
   * Build outbound click tracking options from a Trusted Finder Browse item.
   * @param {Object} item
   * @returns {{ productId: string, condition: string, marketplace: string }}
   */
  outboundTrackOptionsFromItem(item) {
    if (!item || typeof item !== 'object') {
      return { productId: '', condition: '', marketplace: 'ebay' };
    }
    const rawId = item.itemId || item.item_id || item.legacyItemId || '';
    const idStr = String(rawId || '').trim();
    let productId = '';
    if (idStr) {
      productId = idStr.startsWith('ebay_') ? idStr : `ebay_${idStr}`;
    }
    return {
      productId,
      condition: String(item.condition || item.conditionDescription || '').trim(),
      marketplace: 'ebay',
    };
  }

  /**
   * Open an external (eBay) link in a new tab, recording the outbound click and showing
   * the passive marketplace/commission notice.
   * @param {string} url - The URL to open
   * @param {Object} [trackOptions] - Optional outbound click tracking fields
   * @param {string} [trackOptions.productId]
   * @param {string} [trackOptions.condition]
   * @param {string} [trackOptions.marketplace]
   * @returns {{ opened: boolean }}
   */
  openExternalLink(url, trackOptions = {}) {
    if (!url || url === '#') return { opened: false };
    const productId = trackOptions.productId
      || trackOptions.itemId
      || '';
    const trackFn = (typeof trackProductClick === 'function')
      ? trackProductClick
      : (typeof window !== 'undefined' && typeof window.trackProductClick === 'function'
        ? window.trackProductClick
        : null);
    const trackedViaGlobal = Boolean(productId && trackFn);
    if (trackedViaGlobal) {
      trackFn(productId, {
        url,
        marketplace: trackOptions.marketplace || 'ebay',
        condition: trackOptions.condition || '',
      });
    } else if (productId && window.analyticsManager?.trackClick) {
      window.analyticsManager.trackClick(productId, {
        pagePath: window.location.pathname,
        pageTitle: document.title,
        marketplace: trackOptions.marketplace || 'ebay',
        condition: trackOptions.condition || '',
        destinationUrl: url,
      });
    }
    // trackProductClick already beacons; the branches it did not cover — no
    // product id, or the consent-gated AnalyticsManager fallback — would
    // otherwise leave the click unrecorded for anyone without analytics consent.
    if (!trackedViaGlobal) {
      postOutboundClick({
        productId,
        marketplace: trackOptions.marketplace || 'ebay',
        destinationUrl: url,
      });
    }
    window.open(url, '_blank', 'noopener,noreferrer');
    showOutboundNotice(url);
    return { opened: true };
  }

  /**
   * Submit a trusted finder item to the admin review queue
   * @param {Object} itemData - The item data from the trusted finder search
   */
  async submitTrustedFinderItem(itemData) {
    try {
      // Get current user info if available
      const currentUser = window.accountManager?.currentUser || null;
      const userId = currentUser ? String(currentUser.id) : 'anonymous';
      const submittedBy = currentUser 
        ? `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim() || currentUser.username || 'Trusted Finder User'
        : 'Trusted Finder User';

      // Map item data to submission format (condition_id/refurbished_type/ebay_category so admin approval modal has correct pills/indicator)
      const title = itemData.title || itemData.name || 'Untitled Product';
      const price = itemData.price ? `$${parseFloat(itemData.price).toFixed(2)}` : 'N/A';
      const condition = itemData.condition || 'New';
      const url = itemData.itemWebUrl || itemData.item_web_url || '';
      const imageUrl = itemData.imageUrl || itemData.image_url || '';
      const ebayCategoryId = (itemData.ebay_category_id || itemData.ebayCategoryId || '').toString().trim() || null;
      const ebayCategoryPath = (itemData.ebay_category_path || itemData.ebayCategoryPath || '').toString().trim() || null;
      const rawConditionId = itemData.condition_id != null ? itemData.condition_id : (itemData.conditionId != null ? itemData.conditionId : null);
      const conditionId = rawConditionId != null ? (typeof rawConditionId === 'number' ? rawConditionId : parseInt(rawConditionId, 10)) : null;
      const refurbishedType = itemData.refurbished_type || itemData.refurbishedType || null;

      // Prepare submission data. Backend auto-fetches eBay details for trusted_finder and sets condition_id/refurbished_type; sending when present avoids delay and ensures admin modal shows correct condition pill.
      const submissionData = {
        name: title,
        price: price,
        condition: condition,
        url: url,
        image_url: imageUrl || null,
        user_id: userId,
        submitted_by: submittedBy,
        review_status: 'pending',
        source: 'trusted_finder',
        category: '', // Backend will auto-detect
        ...(ebayCategoryId && { ebayCategoryId }),
        ...(ebayCategoryPath && { ebayCategoryPath }),
        ...(conditionId != null && !Number.isNaN(conditionId) && { condition_id: conditionId }),
        ...(refurbishedType && { refurbished_type: refurbishedType }),
        ...((itemData.seller_feedback_pct ?? itemData.sellerFeedbackPct) != null && { seller_feedback_pct: itemData.seller_feedback_pct ?? itemData.sellerFeedbackPct }),
        ...((itemData.seller_sales_count ?? itemData.sellerSalesCount) != null && { seller_sales_count: itemData.seller_sales_count ?? itemData.sellerSalesCount }),
      };

      // Submit to API
      logger.info('[TrustedFinder] Submitting item for admin review:', submissionData);
      const result = await apiService.createSubmission(submissionData);
      logger.info('[TrustedFinder] Submission created successfully:', result);

      // Show success notification (different message if auto-approved)
      const message = result.auto_approved
        ? 'Item approved and added to catalog!'
        : 'Item submitted for admin review!';
      this.showSubmissionNotification(message, 'success');

    } catch (error) {
      logger.error('[TrustedFinder] Error submitting item:', error);
      
      // Show error notification (but don't block eBay link opening)
      const errorMessage = error.message || 'Failed to submit item. Please try again.';
      this.showSubmissionNotification(errorMessage, 'error');
      
      // Re-throw to allow caller to handle if needed
      throw error;
    }
  }

  /**
   * Show a notification for submission status
   * @param {string} message - The notification message
   * @param {string} type - 'success' or 'error'
   */
  showSubmissionNotification(message, type = 'success') {
    // Try to use existing notification system if available
    if (window.accountManager && typeof window.accountManager.showNotification === 'function') {
      window.accountManager.showNotification(message, type);
    } else if (window.adminManager && typeof window.adminManager.showNotification === 'function') {
      window.adminManager.showNotification(message, type);
    } else {
      // Fallback to simple alert
      logger.info(`[TrustedFinder] ${type.toUpperCase()}: ${message}`);
      // Could also create a simple toast notification here
    }
  }

  /**
   * Update pagination controls
   */
  updatePagination(response) {
    const pagination = document.getElementById('trustedPagination');
    const prevBtn = document.getElementById('trustedPrevBtn');
    const nextBtn = document.getElementById('trustedNextBtn');
    const pageInfo = document.getElementById('trustedPageInfo');

    if (!pagination || !prevBtn || !nextBtn) return;

    const hasNext = !!response.next;
    const hasPrev = !!response.previous;
    const count = response.count || 0;

    prevBtn.disabled = !hasPrev;
    nextBtn.disabled = !hasNext;

    if (pageInfo) {
      const start = (this.currentPage - 1) * 20 + 1;
      const end = Math.min(this.currentPage * 20, count);
      
      // Use shorter format on mobile (viewport width <= 768px)
      const isMobile = window.innerWidth <= 768;
      if (isMobile && count > 0) {
        pageInfo.textContent = `${start}-${end} of ${count}`;
      } else {
        pageInfo.textContent = `Page ${this.currentPage} (${start}-${end} of ${count})`;
      }
    }

    if (hasNext || hasPrev) {
      pagination.classList.remove('hidden');
    } else {
      pagination.classList.add('hidden');
    }
  }

  /**
   * Handle pagination
   */
  async handlePagination(direction) {
    if (this.isSearching) return;

    if (direction === 'next') {
      this.currentPage++;
    } else if (direction === 'prev') {
      this.currentPage = Math.max(1, this.currentPage - 1);
    }

    const params = { ...this.currentParams, page: this.currentPage };
    await this.performSearch(params);
  }

  /**
   * Update URL with search parameters
   */
  updateURL(params) {
    const url = new URL(window.location);
    if (params.q) {
      url.searchParams.set('q', params.q);
    } else {
      url.searchParams.delete('q');
    }
    if (params.minPrice) url.searchParams.set('min_price', params.minPrice);
    else url.searchParams.delete('min_price');
    if (params.maxPrice) url.searchParams.set('max_price', params.maxPrice);
    else url.searchParams.delete('max_price');
    if (params.listingType && params.listingType !== 'FIXED_PRICE') url.searchParams.set('listing_type', params.listingType);
    else url.searchParams.delete('listing_type');
    if (params.condition && params.condition !== 'all') url.searchParams.set('condition', params.condition);
    else url.searchParams.delete('condition');
    if (params.returnsAccepted) url.searchParams.set('returns_accepted', 'true');
    else url.searchParams.delete('returns_accepted');
    if (params.freeShipping) url.searchParams.set('free_shipping', 'true');
    else url.searchParams.delete('free_shipping');
    if (params.minQuantity != null && params.minQuantity > 1) url.searchParams.set('min_quantity', params.minQuantity);
    else url.searchParams.delete('min_quantity');
    if (params.includeForParts) url.searchParams.set('include_for_parts', 'true');
    else url.searchParams.delete('include_for_parts');
    if (params.minSellerFeedbackPct) url.searchParams.set('min_feedback', params.minSellerFeedbackPct);
    else url.searchParams.delete('min_feedback');
    if (params.minSellerReviews) url.searchParams.set('min_reviews', params.minSellerReviews);
    else url.searchParams.delete('min_reviews');
    if (params.sortOrder && params.sortOrder !== 'priceAscending') url.searchParams.set('sort_order', params.sortOrder);
    else url.searchParams.delete('sort_order');
    if (params.page > 1) url.searchParams.set('page', params.page);
    else url.searchParams.delete('page');

    window.history.pushState({}, '', url);
  }

  /**
   * Parse URL parameters on page load
   */
  parseURLParams() {
    const urlParams = new URLSearchParams(window.location.search);
    
    // Search query
    const q = urlParams.get('q');
    if (q) {
      const searchInput = document.getElementById('trustedSearchInput');
      if (searchInput) {
        searchInput.value = q;
      }
    }

    // Price filters (values are in dollars, not cents)
    const minPrice = urlParams.get('min_price');
    const maxPrice = urlParams.get('max_price');
    if (minPrice) {
      const minInput = document.getElementById('trustedMinPrice');
      const minSlider = document.getElementById('trustedMinPriceSlider');
      const minDisplay = document.getElementById('trustedMinPriceDisplay');
      const priceValue = parseFloat(minPrice);
      if (minInput) minInput.value = priceValue.toFixed(2);
      if (minSlider) minSlider.value = priceValue;
      if (minDisplay) minDisplay.textContent = `$${priceValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    if (maxPrice) {
      const maxInput = document.getElementById('trustedMaxPrice');
      const maxSlider = document.getElementById('trustedMaxPriceSlider');
      const maxDisplay = document.getElementById('trustedMaxPriceDisplay');
      const priceValue = parseFloat(maxPrice);
      if (maxInput) maxInput.value = priceValue.toFixed(2);
      if (maxSlider) maxSlider.value = priceValue;
      if (maxDisplay) maxDisplay.textContent = `$${priceValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }

    // Listing type (FIXED_PRICE = Buy It Now only checked; BOTH/AUCTION = unchecked)
    const listingType = urlParams.get('listing_type');
    const listingToggle = document.getElementById('trustedListingTypeToggle');
    if (listingToggle) {
      listingToggle.checked = (listingType === 'FIXED_PRICE' || !listingType);
    }

    // Condition
    const condition = urlParams.get('condition');
    if (condition) {
      const conditionRadio = document.querySelector(`input[name="trustedCondition"][value="${condition}"]`);
      if (conditionRadio) {
        conditionRadio.checked = true;
      }
    }

    // Returns accepted (required for all trusted searches)
    const returnsCheckbox = document.getElementById('trustedReturnsAccepted');
    if (returnsCheckbox) returnsCheckbox.checked = true;

    // Free shipping
    const freeShipping = urlParams.get('free_shipping');
    if (freeShipping && ['1', 'true', 'yes'].includes(freeShipping.toLowerCase())) {
      const freeShippingCheckbox = document.getElementById('trustedFreeShipping');
      if (freeShippingCheckbox) freeShippingCheckbox.checked = true;
    }

    // Min quantity
    const minQuantity = urlParams.get('min_quantity');
    if (minQuantity) {
      const qty = parseInt(minQuantity, 10);
      if (!isNaN(qty) && qty >= 1) {
        const minQuantityInput = document.getElementById('trustedMinQuantity');
        if (minQuantityInput) minQuantityInput.value = String(Math.min(999, qty));
      }
    }

    // Include for parts (URL) — sync exclude checkbox
    const includeForParts = urlParams.get('include_for_parts');
    if (includeForParts && ['1', 'true', 'yes'].includes(includeForParts.toLowerCase())) {
      this.setIncludeForPartsInDom(true);
    }

    // Trusted level / min_feedback / min_reviews
    const minFeedbackParam = urlParams.get('min_feedback');
    const minReviewsParam = urlParams.get('min_reviews');
    const levelSelect = document.getElementById('trustedLevelSelect');
    const feedbackInput = document.getElementById('trustedCriteriaFeedback');
    const feedbackSlider = document.getElementById('trustedCriteriaFeedbackSlider');
    const reviewsInput = document.getElementById('trustedCriteriaReviews');
    const reviewsSlider = document.getElementById('trustedCriteriaReviewsSlider');
    const feedbackDisplay = document.getElementById('trustedCriteriaFeedbackDisplay');
    const reviewsDisplay = document.getElementById('trustedCriteriaReviewsDisplay');

    if (minFeedbackParam !== null || minReviewsParam !== null) {
      const feedbackVal = minFeedbackParam !== null ? parseFloat(minFeedbackParam) : null;
      const reviewsVal = minReviewsParam !== null ? parseInt(minReviewsParam, 10) : null;
      const level = feedbackVal != null
        ? Object.keys(this.trustedLevels).find(
            k => this.trustedLevels[k].feedback === feedbackVal && (reviewsVal == null || this.trustedLevels[k].reviews === reviewsVal)
          )
        : null;
      if (level && levelSelect) {
        levelSelect.value = level;
        this.syncPresetToInlineDisplays();
      } else {
        if (feedbackVal != null && !isNaN(feedbackVal) && feedbackVal >= 0 && feedbackVal <= 100) {
          if (feedbackInput) feedbackInput.value = feedbackVal.toFixed(1);
          if (feedbackSlider) feedbackSlider.value = feedbackVal;
          if (feedbackDisplay) feedbackDisplay.textContent = `${feedbackVal.toFixed(1)}`;
        }
        if (reviewsVal != null && !isNaN(reviewsVal) && reviewsVal >= 0) {
          if (reviewsInput) reviewsInput.value = String(reviewsVal);
          if (reviewsSlider) reviewsSlider.value = Math.min(500, reviewsVal);
          if (reviewsDisplay) reviewsDisplay.textContent = String(reviewsVal);
        }
        this.saveCustomCriteriaFromInline();
        this.syncSellerCriteriaUi();
      }
    }

    // Sort order
    const sortOrder = urlParams.get('sort_order');
    const validSorts = ['priceAscending', 'priceDescending', 'sellerScoreDescending', 'newlyListed', 'bestMatch'];
    const sortSelect = document.getElementById('trustedSortOrder');
    if (sortSelect && sortOrder) {
      sortSelect.value = validSorts.includes(sortOrder) ? sortOrder : 'priceAscending';
    }

    // Page
    const page = urlParams.get('page');
    if (page) {
      this.currentPage = parseInt(page, 10) || 1;
    }

    this.ensureReturnsAcceptedRequired();
  }

  /**
   * Show/hide UI states
   */
  showLoadingState(params = {}) {
    const loading = document.getElementById('trustedLoadingState');
    const welcome = document.getElementById('trustedWelcomeState');
    this.setTrustedFinderSlot('loading');
    if (loading) {
      loading.style.display = '';
      loading.classList.remove('hidden');
      
      // Update loading message based on whether seller filters are active
      const loadingText = loading.querySelector('.loading-text');
      if (loadingText) {
        const hasSellerFilters = params.minSellerFeedbackPct || params.minSellerReviews;
        if (hasSellerFilters) {
          loadingText.textContent = 'Verifying seller ratings... This may take a few seconds.';
        } else {
          loadingText.textContent = 'Scanning eBay for top-rated sellers...';
        }
      }
    }
    if (welcome) welcome.classList.add('hidden');
  }

  hideLoadingState() {
    const loading = document.getElementById('trustedLoadingState');
    if (loading) loading.style.display = 'none';
  }

  showResults() {
    const header = document.getElementById('trustedResultsHeader');
    const grid = document.getElementById('trustedResultsGrid');
    const table = document.getElementById('trustedResultsTable');
    const disclosure = document.getElementById('trustedEbayPartnerDisclosure');
    const welcome = document.getElementById('trustedWelcomeState');
    if (header) header.classList.remove('hidden');
    // Only show the active view.
    const oneContainer = this.consoleResultsInOneContainer();
    if (grid) grid.classList.toggle('hidden', !oneContainer && this.currentView !== 'cards');
    if (table) table.classList.toggle('hidden', oneContainer || this.currentView !== 'details');
    if (disclosure) disclosure.classList.remove('hidden');
    if (welcome) welcome.classList.add('hidden');
    this.setTrustedFinderSlot('results');
  }

  hideResults() {
    const header = document.getElementById('trustedResultsHeader');
    const grid = document.getElementById('trustedResultsGrid');
    const table = document.getElementById('trustedResultsTable');
    const disclosure = document.getElementById('trustedEbayPartnerDisclosure');
    if (header) header.classList.add('hidden');
    if (grid) grid.classList.add('hidden');
    if (table) table.classList.add('hidden');
    if (disclosure) disclosure.classList.add('hidden');
  }

  /**
   * Nothing came back — and the two reasons for that are different pages.
   *
   * If the floor emptied it, the query is fine and the fix is one tier down;
   * that is offered as the single action, and only when a looser floor exists.
   * If eBay matched nothing at all, the floor is not the problem and saying
   * anything about it would send the visitor to fix the wrong thing.
   */
  showEmptyState(message) {
    const empty = document.getElementById('trustedEmptyState');
    if (!empty) {
      this.setTrustedFinderSlot('results');
      return;
    }
    const big = empty.querySelector('.big');
    const lede = empty.querySelector('.lede');
    const action = empty.querySelector('[data-tf-lower-floor]');
    const split = this.lastFloorSplit;
    const floorEmptiedIt = !!(split && split.checked && split.matched > 0);
    const preset = this.activePreset();

    if (big) {
      big.textContent = floorEmptiedIt
        ? `0 clear ${preset.label}`
        : 'Nothing matched on eBay';
    }
    if (lede) {
      if (floorEmptiedIt) {
        const hidden = (split.below || 0) + (split.unrated || 0);
        lede.textContent = `${split.matched} matched on eBay · ${hidden} hidden below the floor.`;
      } else {
        lede.textContent = message || 'No listings matched this search.';
      }
    }

    const lower = floorEmptiedIt ? nextFloorDown(getFloor()) : null;
    if (action) {
      if (lower) {
        action.textContent = `Try ${FLOOR_NAMES[lower]} instead`;
        action.hidden = false;
        if (action.dataset.listenerAttached !== 'true') {
          action.dataset.listenerAttached = 'true';
          // setFloor is the whole action: the floor selector, the prose, this
          // page's preset and the re-run all hang off onFloorChange already.
          action.addEventListener('click', () => setFloor(action.dataset.tfLowerFloor || lower));
        }
        action.dataset.tfLowerFloor = lower;
      } else {
        action.hidden = true;
      }
    }

    empty.classList.remove('hidden');
    this.setTrustedFinderSlot('results');
  }

  hideEmptyState() {
    const empty = document.getElementById('trustedEmptyState');
    if (empty) empty.classList.add('hidden');
  }

  showErrorState(message, title = 'Search Error') {
    const error = document.getElementById('trustedErrorState');
    if (error) {
      const msgEl = document.getElementById('trustedErrorMessage');
      if (msgEl && message) msgEl.textContent = message;
      
      // Update title if provided
      const titleEl = error.querySelector('h3');
      if (titleEl && title) titleEl.textContent = title;
      
      error.classList.remove('hidden');
    }
    this.setTrustedFinderSlot('results');
  }

  hideErrorState() {
    const error = document.getElementById('trustedErrorState');
    if (error) error.classList.add('hidden');
  }

  /**
   * Escape HTML to prevent XSS
   */
  escapeHtml(text) {
    if (text === null || text === undefined) return '';
    // Escapes quotes as well as angle brackets: this helper feeds double-quoted
    // HTML attributes, where an unescaped quote closes the attribute early.
    return String(text)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /**
   * Load criteria from localStorage and apply to inline controls, or sync to preset
   */
  loadCustomCriteria() {
    try {
      const stored = localStorage.getItem('usedfindr_trusted_seller_criteria');
      const feedbackInput = document.getElementById('trustedCriteriaFeedback');
      const feedbackSlider = document.getElementById('trustedCriteriaFeedbackSlider');
      const reviewsInput = document.getElementById('trustedCriteriaReviews');
      const reviewsSlider = document.getElementById('trustedCriteriaReviewsSlider');
      const feedbackDisplay = document.getElementById('trustedCriteriaFeedbackDisplay');
      const reviewsDisplay = document.getElementById('trustedCriteriaReviewsDisplay');

      if (stored) {
        const data = JSON.parse(stored);
        const feedbackPct = typeof data.feedbackPct === 'number' ? data.feedbackPct : null;
        const reviews = typeof data.reviews === 'number' && data.reviews >= 0 ? data.reviews : null;
        const valid = feedbackPct != null && feedbackPct >= 0 && feedbackPct <= 100 && reviews != null;

        if (valid) {
          if (feedbackInput) feedbackInput.value = feedbackPct.toFixed(1);
          if (feedbackSlider) feedbackSlider.value = feedbackPct;
          if (reviewsInput) reviewsInput.value = String(reviews);
          if (reviewsSlider) reviewsSlider.value = Math.min(500, reviews);
          if (feedbackDisplay) feedbackDisplay.textContent = `${feedbackPct.toFixed(1)}`;
          if (reviewsDisplay) reviewsDisplay.textContent = String(reviews);
          this.customCriteria = { feedbackPct, reviews, timestamp: data.timestamp };
          return;
        }
        if (!valid) localStorage.removeItem('usedfindr_trusted_seller_criteria');
      }
      this.customCriteria = null;
      this.syncPresetToInlineDisplays();
    } catch (error) {
      logger.error('[TrustedFinder] Error loading custom criteria:', error);
      this.customCriteria = null;
      this.syncPresetToInlineDisplays();
    }
  }

  /**
   * Sync preset values to inline inputs/displays (e.g. when Trusted Level dropdown or Reset is used)
   */
  syncPresetToInlineDisplays() {
    const trustedLevelSelect = document.getElementById('trustedLevelSelect');
    const feedbackInput = document.getElementById('trustedCriteriaFeedback');
    const feedbackSlider = document.getElementById('trustedCriteriaFeedbackSlider');
    const reviewsInput = document.getElementById('trustedCriteriaReviews');
    const reviewsSlider = document.getElementById('trustedCriteriaReviewsSlider');
    const feedbackDisplay = document.getElementById('trustedCriteriaFeedbackDisplay');
    const reviewsDisplay = document.getElementById('trustedCriteriaReviewsDisplay');

    const level = trustedLevelSelect ? trustedLevelSelect.value : 'strict';
    const config = this.trustedLevels[level] || this.trustedLevels.strict;

    if (feedbackInput) feedbackInput.value = config.feedback.toFixed(1);
    if (feedbackSlider) feedbackSlider.value = config.feedback;
    if (feedbackDisplay) feedbackDisplay.textContent = `${config.feedback.toFixed(1)}`;
    if (reviewsInput) reviewsInput.value = String(config.reviews);
    if (reviewsSlider) reviewsSlider.value = Math.min(500, config.reviews);
    if (reviewsDisplay) reviewsDisplay.textContent = String(config.reviews);
    this.ensureReturnsAcceptedRequired();
  }

  /**
   * Save current criteria values from inline controls to localStorage
   */
  saveCustomCriteriaFromInline() {
    const feedbackInput = document.getElementById('trustedCriteriaFeedback');
    const reviewsInput = document.getElementById('trustedCriteriaReviews');

    const feedbackPct = feedbackInput ? parseFloat(feedbackInput.value) : 99.5;
    const reviews = reviewsInput ? parseInt(reviewsInput.value, 10) : 50;

    const data = {
      feedbackPct: isNaN(feedbackPct) ? 99.5 : Math.max(0, Math.min(100, feedbackPct)),
      reviews: isNaN(reviews) || reviews < 0 ? 50 : reviews,
      timestamp: Date.now()
    };
    this.customCriteria = data;
    localStorage.setItem('usedfindr_trusted_seller_criteria', JSON.stringify(data));
  }

  /**
   * Live header badge + reset affordance vs selected trusted preset / custom overrides.
   */
  syncSellerCriteriaUi() {
    this.updateTrustedLevelState();
    this.updatePresetBadge();
    this.updateFiltersDirtyAndReset();
    this.updateFilterSummaryChips();
  }

  updatePresetBadge() {
    const el = document.querySelector('[data-tf-preset-badge]');
    if (!el) return;
    const trustedLevelSelect = document.getElementById('trustedLevelSelect');
    const feedbackInput = document.getElementById('trustedCriteriaFeedback');
    const reviewsInput = document.getElementById('trustedCriteriaReviews');
    const returnsCheckbox = document.getElementById('trustedReturnsAccepted');
    const fb = feedbackInput ? parseFloat(feedbackInput.value) : 99.5;
    const rev = reviewsInput ? parseInt(reviewsInput.value, 10) : 50;
    const pct = Number.isNaN(fb) ? 99.5 : Math.max(0, Math.min(100, fb));
    const revN = Number.isNaN(rev) || rev < 0 ? 50 : rev;
    const names = { standard: 'Standard', strict: 'Strict', elite: 'Elite' };
    const levelLabel = trustedLevelSelect && trustedLevelSelect.disabled
      ? 'Custom'
      : (names[trustedLevelSelect?.value] || 'Strict');

    el.classList.remove('is-custom', 'is-loose');
    if (trustedLevelSelect && trustedLevelSelect.disabled) {
      el.classList.add('is-custom');
    } else if (trustedLevelSelect && trustedLevelSelect.value === 'standard') {
      el.classList.add('is-loose');
    }

    const lvlEl = el.querySelector('.preset-badge__level');
    const fbEl = el.querySelector('.preset-badge__fb');
    const rvEl = el.querySelector('.preset-badge__rv');
    const returnsEl = el.querySelector('.preset-badge__returns');
    if (lvlEl) lvlEl.textContent = levelLabel;
    if (fbEl) fbEl.textContent = pct % 1 === 0 ? String(Math.round(pct)) : pct.toFixed(1);
    if (rvEl) rvEl.textContent = String(revN);
    if (returnsEl) returnsEl.textContent = 'Required';
  }

  /**
   * Sync collapsed filter summary chips from current DOM filter state.
   */
  updateFilterSummaryChips() {
    const priceChip = document.querySelector('[data-summary="price"]');
    const conditionChip = document.querySelector('[data-summary="condition"]');
    const listingChip = document.querySelector('[data-summary="listing"]');
    if (!priceChip && !conditionChip && !listingChip) return;

    const minInput = document.getElementById('trustedMinPrice');
    const maxInput = document.getElementById('trustedMaxPrice');
    const minVal = minInput && minInput.value !== '' ? parseFloat(minInput.value) : 0;
    const maxVal = maxInput && maxInput.value !== '' ? parseFloat(maxInput.value) : 10000;
    const hasMin = !Number.isNaN(minVal) && minVal > 0;
    const hasMax = !Number.isNaN(maxVal) && maxVal < 10000;

    let priceLabel = 'Any price';
    if (hasMin && hasMax) {
      priceLabel = `$${Math.round(minVal).toLocaleString('en-US')}–$${Math.round(maxVal).toLocaleString('en-US')}`;
    } else if (hasMin) {
      priceLabel = `$${Math.round(minVal).toLocaleString('en-US')}+`;
    } else if (hasMax) {
      priceLabel = `Up to $${Math.round(maxVal).toLocaleString('en-US')}`;
    }
    if (priceChip) priceChip.textContent = priceLabel;

    const conditionRadio = document.querySelector('input[name="trustedCondition"]:checked');
    const conditionVal = conditionRadio ? conditionRadio.value : 'all';
    const conditionLabels = {
      all: 'All conditions',
      New: 'New',
      Refurb: 'Refurb',
      Used: 'Used'
    };
    if (conditionChip) conditionChip.textContent = conditionLabels[conditionVal] || 'All conditions';

    const listingToggle = document.getElementById('trustedListingTypeToggle');
    if (listingChip) {
      listingChip.textContent = listingToggle && listingToggle.checked ? 'Buy It Now only' : 'All listings';
    }
  }

  /**
   * Has anything in the pane's Refine group been moved off its default?
   *
   * The defaults are: no price bounds, any condition, Buy It Now on, free
   * shipping off. Buy It Now is the odd one — its default is *checked*, so
   * "dirty" for that row means unchecked.
   */
  refineGroupIsSet() {
    const value = (id) => (document.getElementById(id) || {}).value || '';
    if (value('trustedMinPrice').trim() !== '') return true;
    if (value('trustedMaxPrice').trim() !== '') return true;
    const anyCondition = document.getElementById('trustedCondAll');
    if (anyCondition && !anyCondition.checked) return true;
    const binOnly = document.getElementById('trustedListingTypeToggle');
    if (binOnly && !binOnly.checked) return true;
    const freeShipping = document.getElementById('trustedFreeShipping');
    if (freeShipping && freeShipping.checked) return true;
    return false;
  }

  updateFiltersDirtyAndReset() {
    const panel = document.getElementById('trustedFiltersPanel');
    const btn = document.getElementById('trustedResetToPresetBtn');
    const tfRoot = document.getElementById('tfRoot');
    const dirtyPill = document.querySelector('#filtersHead .dirty');
    if (!panel || !btn) return;
    /*
     * "Dirty" is now the Refine group, because that is what `reset` clears.
     *
     * It used to mean "the custom feedback/review pair differs from the
     * preset", which was right when the button said "Reset to preset" and sat
     * beside those two sliders. Under v3 those sliders have no control on the
     * page and always equal the preset, so that test was permanently false and
     * the button was permanently disabled -- a control that could never fire,
     * under a heading offering to clear four filters that were plainly set.
     *
     * The custom pair is still counted: a visitor who set one before this
     * change still has it in localStorage, and `reset` still clears it.
     */
    const sel = document.getElementById('trustedLevelSelect');
    const fbIn = document.getElementById('trustedCriteriaFeedback');
    const rvIn = document.getElementById('trustedCriteriaReviews');
    let differs = false;
    if (sel && sel.disabled) {
      differs = true;
    } else {
      const level = sel ? sel.value : 'strict';
      const cfg = this.trustedLevels[level] || this.trustedLevels.strict;
      const fb = fbIn ? parseFloat(fbIn.value) : NaN;
      const rv = rvIn ? parseInt(rvIn.value, 10) : NaN;
      const fbMismatch = !Number.isNaN(fb) && fb !== cfg.feedback;
      const rvMismatch = !Number.isNaN(rv) && rv >= 0 && rv !== cfg.reviews;
      differs = fbMismatch || rvMismatch;
    }
    if (!differs) differs = this.refineGroupIsSet();
    panel.dataset.tfDirty = differs ? 'true' : 'false';
    if (tfRoot) tfRoot.dataset.dirty = differs ? 'true' : 'false';
    if (dirtyPill) dirtyPill.hidden = !differs;
    btn.disabled = !differs;
    btn.setAttribute('aria-disabled', differs ? 'false' : 'true');
  }

  setTrustedFinderSlot(state) {
    const el = document.querySelector('.trusted-results-container');
    const tfRoot = document.getElementById('tfRoot');
    if (el && (state === 'empty' || state === 'loading' || state === 'results')) {
      el.setAttribute('data-tf-slot-state', state);
    }
    if (tfRoot && (state === 'empty' || state === 'loading' || state === 'results')) {
      tfRoot.setAttribute('data-state', state);
    }
  }

  /**
   * Update Trusted Level dropdown state and override message when any value differs from preset
   */
  updateTrustedLevelState() {
    const trustedLevelSelect = document.getElementById('trustedLevelSelect');
    const overrideMessage = document.getElementById('trustedLevelOverrideMessage');
    const feedbackInput = document.getElementById('trustedCriteriaFeedback');
    const reviewsInput = document.getElementById('trustedCriteriaReviews');

    if (!trustedLevelSelect) return;

    const level = trustedLevelSelect.value || 'strict';
    const config = this.trustedLevels[level] || this.trustedLevels.strict;
    const feedbackVal = feedbackInput ? parseFloat(feedbackInput.value) : NaN;
    const reviewsVal = reviewsInput ? parseInt(reviewsInput.value, 10) : NaN;
    const feedbackDiffers = !isNaN(feedbackVal) && feedbackVal !== config.feedback;
    const reviewsDiffers = !isNaN(reviewsVal) && reviewsVal >= 0 && reviewsVal !== config.reviews;
    const anyOverride = feedbackDiffers || reviewsDiffers;

    if (anyOverride) {
      trustedLevelSelect.disabled = true;
      trustedLevelSelect.classList.add('trusted-level-disabled');
      trustedLevelSelect.setAttribute('aria-label', 'Trusted level: Custom');
      if (overrideMessage) {
        const pct = feedbackInput ? parseFloat(feedbackInput.value) : 99.5;
        const rev = reviewsInput ? parseInt(reviewsInput.value, 10) : 50;
        overrideMessage.textContent = `Using custom seller criteria (${isNaN(pct) ? '—' : pct.toFixed(1)}% + ${isNaN(rev) ? '—' : rev} reviews)`;
        overrideMessage.classList.remove('hidden');
      }
    } else {
      trustedLevelSelect.disabled = false;
      trustedLevelSelect.classList.remove('trusted-level-disabled');
      trustedLevelSelect.removeAttribute('aria-label');
      if (overrideMessage) {
        overrideMessage.textContent = '';
        overrideMessage.classList.add('hidden');
      }
    }
  }

  /**
   * Reset to preset: sync inline values to selected preset, clear localStorage
   */
  resetToDefaults() {
    this.customCriteria = null;
    localStorage.removeItem('usedfindr_trusted_seller_criteria');

    this.syncPresetToInlineDisplays();
    this.ensureReturnsAcceptedRequired();
    this.saveCustomCriteriaFromInline();
    this.syncSellerCriteriaUi();

    /*
     * Clear the Refine group as well, which is what the control now says it
     * does. It used to reset only the custom feedback/review pair -- a control
     * labelled "Reset to preset" beside an "Advanced settings" panel. In v3 the
     * pane's `reset` sits in the Refine group's own heading, and the four
     * things under that heading are what a visitor is asking it to undo.
     *
     * The preset is deliberately not touched: it is the trust floor, it is
     * global, and clearing it from here would change pages the visitor is not
     * looking at. That is why this reads the group rather than calling a
     * blanket form reset.
     */
    const minPrice = document.getElementById('trustedMinPrice');
    const maxPrice = document.getElementById('trustedMaxPrice');
    if (minPrice) minPrice.value = '';
    if (maxPrice) maxPrice.value = '';
    const anyCondition = document.getElementById('trustedCondAll');
    if (anyCondition) anyCondition.checked = true;
    const binOnly = document.getElementById('trustedListingTypeToggle');
    if (binOnly) binOnly.checked = true;   // the page's default, not "off"
    const freeShipping = document.getElementById('trustedFreeShipping');
    if (freeShipping) freeShipping.checked = false;

    this.syncDualPriceVisual();
    this.updateFilterSummaryChips();
    this.updateFiltersDirtyAndReset();

    if (this.currentParams.q) this.handleSearch();
  }

  /**
   * Update sandbox mode indicator
   */
  updateSandboxIndicator() {
    const indicator = document.getElementById('trustedSandboxIndicator');
    if (indicator) {
      if (this.isSandboxMode) {
        indicator.classList.remove('hidden');
      } else {
        indicator.classList.add('hidden');
      }
    }
  }
}

// Initialize when DOM is ready
let trustedFinderManager;

function initializeTrustedFinder() {
  logger.info('[TrustedFinder] initializeTrustedFinder readyState:', document.readyState);

  trustedFinderManager = new TrustedFinderManager();
  trustedFinderManager.init();
  window.trustedFinderManager = trustedFinderManager;
}

if (document.readyState === 'loading') {
  logger.info('[TrustedFinder] Document still loading, waiting for DOMContentLoaded');
  document.addEventListener('DOMContentLoaded', () => {
    logger.info('[TrustedFinder] DOMContentLoaded fired');
    initializeTrustedFinder();
  });
} else {
  logger.info('[TrustedFinder] Document already ready, initializing immediately');
  initializeTrustedFinder();
}

// Service worker registration
async function initializeServiceWorker() {
  // Prevent multiple registration attempts
  if (window.__swRegistrationAttempted) {
    return;
  }
  window.__swRegistrationAttempted = true;

  try {
    // Detect production vs development
    const isProduction = window.location.hostname !== 'localhost' &&
      window.location.hostname !== '127.0.0.1' &&
      window.location.protocol === 'https:';

    // Get static URL from body data attribute or use default
    const body = document.body;
    const staticUrl = (body?.dataset?.staticUrl || '/static/').replace(/\/$/, '');
    window.STATIC_URL = staticUrl;

    // Build paths for sw-registration.js
    // 1. Prefer explicit URL from template (vite_asset_url = hashed path when manifest exists)
    // 2. /static/src/js/ 404s in production - Vite emits hashed assets to assets/, not src/
    const explicitSwUrl = body?.dataset?.swRegistrationUrl?.trim();
    let possiblePaths = explicitSwUrl
      ? [explicitSwUrl]
      : isProduction
        ? [`${staticUrl}/src/js/sw-registration.js`]
        : [
            '/src/js/sw-registration.js',
            `${staticUrl}/src/js/sw-registration.js`
          ].filter(Boolean);

    let lastError = null;
    for (const swPath of possiblePaths) {
      try {
        const { registerServiceWorker } = await import(/* @vite-ignore */ swPath);
        registerServiceWorker();
        return;
      } catch (err) {
        lastError = err;
        continue;
      }
    }

    // Fallback: fetch manifest and resolve hashed sw-registration path (when template fallback 404s)
    if (isProduction && possiblePaths.some(p => p && p.includes('/src/js/'))) {
      try {
        const base = (staticUrl || '/static/').replace(/\/+$/, '');
        const manifestUrl = `${base}/vite-manifest.json`;
        const res = await fetch(manifestUrl);
        if (res.ok) {
          const manifest = await res.json();
          const chunk = manifest['sw-registration'] || manifest['src/js/sw-registration.js'];
          if (chunk?.file) {
            const hashedPath = `${base}/${chunk.file.replace(/^\//, '')}`;
            const { registerServiceWorker } = await import(/* @vite-ignore */ hashedPath);
            registerServiceWorker();
            return;
          }
        }
      } catch (manifestErr) {
        logger.warn('[TrustedFinder] Manifest fallback failed:', manifestErr);
      }
    }

    logger.warn('[TrustedFinder] Service worker registration failed for all paths:', possiblePaths, lastError);
  } catch (error) {
    logger.warn('[TrustedFinder] Service worker registration failed:', error);
  }
}

// Initialize service worker after DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeServiceWorker);
} else {
  initializeServiceWorker();
}

// Also try after a short delay as fallback
setTimeout(() => {
  if (!window.trustedFinderManager) {
    logger.warn('[TrustedFinder] Manager not initialized after timeout, retrying...');
    initializeTrustedFinder();
  }
}, 500);

export { TrustedFinderManager };
